import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import './ReportDemo.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface StepState {
  stepNumber: number;
  status: 'locked' | 'ready' | 'running' | 'success' | 'error';
  requestData: Record<string, any>;
  responseData: Record<string, any> | null;
  errorMessage: string | null;
  startTime: Date | null;
  endTime: Date | null;
}

interface Project {
  id: number;
  project_name: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  city?: string | null;
  state?: string | null;
}

interface Layout {
  id: number;
  name: string;
}

// Matches GET /configurations/?layout_id={id} results[]
interface RunConfig {
  id: number;
  configuration_name: string;
  status: string;
  jobs_count: number;
  created_date?: string;
}

interface PrereqItem {
  configId: number;
  configName: string;
  heatmapStatus: 'checking' | 'pending' | 'running' | 'completed' | 'error';
  pileRevealStatus: 'checking' | 'pending' | 'running' | 'completed' | 'not_available' | 'error';
}

interface InputFile {
  filename: string;
  format: string;
  dated: string;
}

interface Revision {
  rev: string;
  date: string;
  description: string;
  preparedBy: string;
  checkedBy: string;
  approvedBy: string;
}

interface PileBin {
  start: number;
  end: number;
  count: number;
  percentage: number;
  color?: string;
  name?: string;
  category?: string;
  section_type?: string;
  minimum_embedment?: number;
  pile_length?: number;
  total_weight?: number;
}

interface PileBinningEntry {
  guid: string;
  unit: string;
  source: string;
  analysis: any[];
}

function genGuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STEP_TITLES = [
  'Authorize',
  'Select Project, Layout & Configurations',
  'Select Figures',
  'Check Prerequisites',
  'Configure Pile Binning',
  'Configure Report',
  'Generate Report',
  'Download Report',
];

const PROGRESS_LABELS = ['Authorize', 'Select Resources', 'Figures', 'Prerequisites', 'Pile Binning', 'Configure', 'Generate', 'Download'];

const MAX_FIGURE_CONFIGS = 3;

const PREREQ_LABELS: Record<string, string> = {
  checking: 'Checking…',
  pending: 'Queued',
  running: 'Processing…',
  completed: 'Ready',
  not_available: 'N/A',
  error: 'Error',
};

const STEEL_SECTIONS = [
  'W8x35','W8x31','W8x28','W8x24','W8x21','W8x18','W8x15','W8x10',
  'W6x25','W6x20','W6x16','W6x15','W6x12','W6x10.4','W6x9','W6x8.5','W6x7.75','W6x7',
];

const PILE_COLORS_PALETTE = [
  '#4895EF','#FFB703','#2A9D8F','#BC6C25','#F94144','#06D6A0',
  '#4361EE','#6A4C93','#F4A261','#277DA1','#E63946','#957FEF',
  '#FF9F1C','#43AA8B','#D62828','#4D908E','#577590','#90BE6D',
  '#1B998B','#F3722C','#264653','#FF6B6B','#577399',
];

const CSV_PILE_TYPE_COLORS: Record<string, string> = {
  'Standard Array Pier':      '#4895ef',
  'Standard Array Pier_End':  '#bc6c25',
  'Heavy Array Pier':         '#ffb703',
  'Standard Motor Pier':      '#f94144',
  'Standard Array Pier_Edge': '#2a9d8f',
  'Heavy Motor Pier':         '#06d6a0',
};

const DP_DXF_PILE_TYPE_COLORS: Record<string, string> = {
  'Standard_Array_Pier':      '#4895ef',
  'Heavy_Array_Pier':         '#bc6c25',
  'Standard_Array_Pier_Edge': '#2900a5',
  'Heavy_Array_Pier_Edge':    '#f94144',
  'Standard_Motor_Pier':      '#ffb703',
  'Heavy_Motor_Pier':         '#06d6a0',
};

function colorFromNameHash(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) - h) + name.charCodeAt(i);
    h = h & h;
  }
  const r = (h & 0xFF0000) >> 16;
  const g = (h & 0x00FF00) >> 8;
  const b =  h & 0x0000FF;
  return `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;
}

// Decimal degrees → "39°53'56\" N" / "4°43'07\" W". Seconds are truncated (not
// rounded) to match the reference format the backend template expects.
function toDMS(decimal: number, isLat: boolean): string {
  const abs = Math.abs(decimal);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = Math.floor((minFloat - min) * 60);
  const dir = isLat ? (decimal >= 0 ? 'N' : 'S') : (decimal >= 0 ? 'E' : 'W');
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${deg}°${pad(min)}'${pad(sec)}" ${dir}`;
}

// Ports analysis-tab.component.ts's parseLatLng — accepts either a raw decimal
// or an already-DMS-formatted string (e.g. from a previously-saved report,
// which stores the DMS string, not the decimal) and normalizes both to decimal.
function parseLatLng(value: any, isLat: boolean): { decimal: number; dms: string } {
  if (value === null || value === undefined || value === '') return { decimal: NaN, dms: '' };
  const num = Number(value);
  if (!isNaN(num)) return { decimal: num, dms: toDMS(num, isLat) };
  const str = String(value);
  const match = str.match(/(\d+)°(\d+)'(\d+)"\s*([NSEWnsew])/);
  if (match) {
    const decimal = Number(match[1]) + Number(match[2]) / 60 + Number(match[3]) / 3600;
    const signed = /[SsWw]/i.test(match[4]) ? -decimal : decimal;
    return { decimal: signed, dms: str };
  }
  return { decimal: NaN, dms: str };
}

type UnitSystem = 'SI' | 'Imperial';

// Ports analysis-tab.component.ts's updateUnitInEarthworkData / updateUnitConversionInEarthworkData.
// Raw earthWorkData from the DB is in cubic meters/sq meters/meters (or the
// Imperial equivalents) when `isUpdatedVersion` is set — otherwise it's the
// legacy shape, keyed only on selectedUnit with different factors again.
// `disturbedPercent` is a ratio and is never converted, in either path.
function convertEarthworkData(
  earthWork: Record<string, any> | undefined,
  sourceUnit: UnitSystem,
  selectedUnit: UnitSystem
): Record<string, any> {
  if (!earthWork || Object.keys(earthWork).length === 0) return {};

  const volumeIn: Record<string, number> = earthWork.volume ?? {};
  const areaIn: Record<string, number> = earthWork.area ?? {};
  const depthIn: Record<string, number> = earthWork.depth ?? {};
  const volume: Record<string, number> = {};
  const area: Record<string, number> = {};
  const depth: Record<string, number> = {};

  const mapKeys = (
    src: Record<string, number>,
    dst: Record<string, number>,
    excludeKey: string | null,
    factor: (v: number) => number
  ) => {
    Object.keys(src).forEach(key => { dst[key] = key !== excludeKey ? factor(src[key]) : src[key]; });
  };

  if (earthWork.isUpdatedVersion) {
    if (sourceUnit === 'Imperial') {
      if (selectedUnit === 'Imperial') {
        mapKeys(volumeIn, volume, 'cutFillRatio', v => v / 27);                      // cubic ft -> cubic yd
        mapKeys(areaIn, area, 'disturbedPercent', v => v * 0.000022956840904921);    // sq ft -> acre
        mapKeys(depthIn, depth, null, v => v);                                       // already ft
      } else {
        mapKeys(volumeIn, volume, 'cutFillRatio', v => v / 35.315);                  // cubic ft -> cubic m
        mapKeys(areaIn, area, 'disturbedPercent', v => v / 107600);                  // sq ft -> ha
        mapKeys(depthIn, depth, null, v => v / 3.281);                               // ft -> m
      }
    } else {
      if (selectedUnit === 'Imperial') {
        mapKeys(volumeIn, volume, 'cutFillRatio', v => v * 1.30795);                 // cubic m -> cubic yd
        mapKeys(areaIn, area, 'disturbedPercent', v => v / 4047);                    // sq m -> acre
        mapKeys(depthIn, depth, null, v => v * 3.28084);                            // m -> ft
      } else {
        mapKeys(volumeIn, volume, null, v => v);                                     // already cubic m
        mapKeys(areaIn, area, 'disturbedPercent', v => v / 10000);                   // sq m -> ha
        mapKeys(depthIn, depth, null, v => v);                                       // already m
      }
    }
  } else {
    // Legacy path — keyed only on selectedUnit (source assumed already hectares/m for SI).
    mapKeys(volumeIn, volume, 'cutFillRatio', v => selectedUnit === 'SI' ? v : v * 1.30795);
    mapKeys(areaIn, area, 'disturbedPercent', v => selectedUnit === 'SI' ? v : v * 2.471);
    mapKeys(depthIn, depth, null, v => selectedUnit === 'SI' ? v : v * 3.28084);
  }

  return { volume, area, depth };
}

// Ports home-page.component.ts's generateDescendingLegendRanges — splits [min, max]
// into `intervals` equal-width bins, listed largest-first.
function generateDescendingLegendRanges(
  min: number,
  max: number,
  intervals = 5
): Array<{ start: number; end: number }> {
  if (min === max) return [{ start: min, end: max }];
  const step = (max - min) / intervals;
  const ranges: Array<{ start: number; end: number }> = [];
  for (let i = intervals; i > 0; i--) {
    ranges.push({
      start: parseFloat((min + step * (i - 1)).toFixed(2)),
      end: parseFloat((min + step * i).toFixed(2)),
    });
  }
  return ranges;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ReportDemo: React.FC = () => {
  const { authToken, setAuthToken, userInfo, setUserInfo } = useAuth();
  const [searchParams] = useSearchParams();
  const [localToken, setLocalToken] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emailStatus, setEmailStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error'; message: string }>({ type: 'idle', message: '' });

  // Step state machine
  const [steps, setSteps] = useState<StepState[]>([
    { stepNumber: 1, status: 'ready',  requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 2, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 3, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 4, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 5, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 6, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 7, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    { stepNumber: 8, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
  ]);
  // One card element per step, indexed by stepNumber - 1; used to auto-scroll to
  // a step as soon as it's unlocked (see unlockStep/scrollToStep below).
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Step 2 — cascade selector
  const [projectsList, setProjectsList]     = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectSearch, setProjectSearch]   = useState('');
  const [projDropOpen, setProjDropOpen]     = useState(false);
  const projDropRef = useRef<HTMLDivElement>(null);

  const [layoutsList, setLayoutsList]       = useState<Layout[]>([]);
  const [layoutsLoading, setLayoutsLoading] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<Layout | null>(null);
  const [layoutDropOpen, setLayoutDropOpen] = useState(false);
  const layoutDropRef = useRef<HTMLDivElement>(null);
  // sourceUnit ('SI'/'Imperial', the layout's storage convention) and selectedUnit
  // ('SI'/'Imperial', the display preference — TerrainProLayouts.unit_selection /
  // TerrainProUserSettings.settings['unit']) are both fetched from
  // get_configurations_of_layout at report-generation time — see generateReport().

  const [configsList, setConfigsList]       = useState<RunConfig[]>([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [selectedConfigIds, setSelectedConfigIds] = useState<Set<number>>(new Set());
  // Input values (minPileReveal, revealWindow, maxSlopeChange, maxTTAxisSlope,
  // totalGrading) shown alongside each config in Step 2 and Step 3 — fetched
  // once per layout via the same endpoint generateReport() uses.
  const [configDetailsById, setConfigDetailsById] = useState<Record<number, Record<string, any>>>({});

  // Step 3 — figure selection (max MAX_FIGURE_CONFIGS)
  const [selectedFigureConfigIds, setSelectedFigureConfigIds] = useState<Set<number>>(new Set());

  // Step 4 — prerequisites
  const [prereqItems, setPrereqItems]       = useState<Record<number, PrereqItem>>({});
  const prereqRef     = useRef<Record<number, PrereqItem>>({});
  const prereqPollRef = useRef<NodeJS.Timeout | null>(null);
  const epsgRef       = useRef<string>('4326');
  // Captured from the /tools/api/v1/terrain-pro/reports/tiles response — mirrors
  // production's tilesResponse?.bounds?.bounds. Stays null if no heatmap trigger returned bounds.
  const tilesBoundsRef = useRef<Record<string, any> | null>(null);
  // Request/response payloads for the heatmap-status poll (GET, layout-wide) and
  // the per-config generate-pile-reveal call (POST) — surfaced in renderStep4()
  // alongside the table, same "View Request Payload" / "View Response Data"
  // pattern used by the Pile Binning step. heatmap-status is polled repeatedly,
  // so only the most recent call is kept.
  const [heatmapStatusApiLog, setHeatmapStatusApiLog] = useState<{ request: any; response: any } | null>(null);
  const [pileRevealApiLog, setPileRevealApiLog] = useState<Record<number, { request: any; response: any }>>({});
  // Always-current token ref — avoids stale closure when called from setInterval
  const authTokenRef  = useRef<string>(authToken ?? '');

  // Step 5 — pile binning
  const [pileBins, setPileBins] = useState<Record<number, PileBinningEntry>>({});
  const [pileBinsLoading, setPileBinsLoading] = useState<Record<number, boolean>>({});
  const [pileBinsError, setPileBinsError] = useState<Record<number, string | null>>({});
  // Request/response payloads for the get-pile-binning-data call, per config —
  // surfaced in renderStep4() alongside the table (same "View Request Payload" /
  // "View Response Data" pattern as the API Demo's InteractiveDemo.tsx).
  const [pileBinningApiLog, setPileBinningApiLog] = useState<Record<number, { request: any; response: any }>>({});
  // Request/response payloads for the Auto Save (auto_save) and Split
  // (split_range) calls, per config — each call overwrites its config's
  // entry so the panel always shows only the most recent call.
  const [autoSaveApiLog, setAutoSaveApiLog] = useState<Record<number, { request: any; response: any }>>({});
  const [splitApiLog, setSplitApiLog] = useState<Record<number, { request: any; response: any }>>({});
  const [activePileBinTab, setActivePileBinTab] = useState(0);
  const [splitEditing, setSplitEditing] = useState<{ configId: number; pileKey: string; binIdx: number } | null>(null);
  const [splitInputVal, setSplitInputVal] = useState('');
  const colorInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const catTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const labelTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pileBinsRef = useRef<Record<number, PileBinningEntry>>({});

  // Step 6 — report config
  const [reportType] = useState<'advanced' | 'earthwork' | 'comparison'>('advanced');
  const [format] = useState<'pdf' | 'docx'>('docx');
  const [docxVersion] = useState<'template' | 'legacy'>('template');
  const [projName, setProjName]     = useState('');
  const [projLocation, setProjLocation] = useState('');
  const [coordSys, setCoordSys]     = useState('');
  const [latitude, setLatitude]     = useState('');
  const [longitude, setLongitude]   = useState('');
  const [inputFiles, setInputFiles] = useState<InputFile[]>([{ filename: '', format: '', dated: new Date().toISOString().slice(0, 10) }]);
  const [revisions, setRevisions]   = useState<Revision[]>([]);
  const [metaOpen, setMetaOpen]     = useState(true);
  const [filesOpen, setFilesOpen]   = useState(false);
  const [revOpen, setRevOpen]       = useState(false);

  // Step 7 — generation
  const [reportStatusMsg, setReportStatusMsg] = useState('');
  const genPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Step 8 — download
  const [downloadUrl, setDownloadUrl]       = useState('');
  const [downloadFilename, setDownloadFilename] = useState('');

  // ── Request helpers ────────────────────────────────────────────────────────

  const authHeaders = useCallback((): HeadersInit => ({
    Authorization: `Bearer ${authToken}`,
    Accept: 'application/json',
  }), [authToken]);

  const jsonHeaders = useCallback((): HeadersInit => ({
    Authorization: `Bearer ${authToken}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }), [authToken]);

  // Sets the token AND resolves userInfo.user_email (used as `email` in the
  // report payload) — the "API Demo" page's handleAuthenticate() does this too,
  // but reads authToken from its own closure so it can't be reused directly here.
  // Returns whether auth-info actually validated the token, so callers (Step 1)
  // can gate success/unlock on a real check instead of just "a token was entered".
  async function applyAuthToken(token: string): Promise<boolean> {
    setAuthToken(token);
    try {
      const r = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/auth-info`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (r.ok) {
        const d = await r.json();
        setUserInfo({
          user_email: d.user || '',
          user_uuid: d.cognito_uuid || '',
          cognito_uuid: d.cognito_uuid || '',
          user: d.user || '',
          ...d,
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('[ReportDemo] auth-info fetch failed:', err);
      return false;
    }
  }

  // Step 1 — email login: exchanges a registered email for a token via the
  // same /email-login endpoint the API Demo's Step 1 uses.
  async function handleEmailLogin() {
    if (!emailInput.trim()) {
      setEmailStatus({ type: 'error', message: 'Please enter an email address' });
      return;
    }
    setEmailStatus({ type: 'loading', message: 'Looking up account...' });
    try {
      const r = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/email-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput.trim() }),
      });
      const d = await r.json();
      if (r.ok && d.token) {
        setEmailStatus({ type: 'success', message: `Token retrieved for ${d.user}` });
        setLocalToken(d.token);
        await handleAuthorize(d.token);
      } else {
        setEmailStatus({ type: 'error', message: d.error || 'Failed to retrieve token' });
      }
    } catch (err: any) {
      setEmailStatus({ type: 'error', message: err.message || 'Network error' });
    }
  }

  // Step 1 — validates the token via applyAuthToken() and only marks the step
  // successful (unlocking Step 2) if auth-info actually confirmed it.
  async function handleAuthorize(token: string) {
    updateStep(1, { status: 'running', errorMessage: null, startTime: new Date() });
    const ok = await applyAuthToken(token);
    if (ok) {
      updateStep(1, { status: 'success', endTime: new Date() });
      unlockStep(2);
    } else {
      updateStep(1, { status: 'error', errorMessage: 'Token validation failed — check the token and try again.', endTime: new Date() });
    }
  }

  // ── Step state helpers ────────────────────────────────────────────────────

  function updateStep(num: number, patch: Partial<StepState>) {
    setSteps(prev => prev.map(s => s.stepNumber === num ? { ...s, ...patch } : s));
  }

  function unlockStep(num: number) {
    setSteps(prev => prev.map(s =>
      s.stepNumber === num && s.status === 'locked' ? { ...s, status: 'ready' } : s
    ));
    // Carry the user straight to the panel they just unlocked. Locked cards only
    // render a placeholder, so wait a tick for the real step body to mount before
    // measuring its position — an immediate scrollIntoView would target the
    // placeholder's (smaller) layout and undershoot.
    scrollToStep(num);
  }

  function scrollToStep(num: number) {
    setTimeout(() => {
      stepRefs.current[num - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  function getStep(num: number): StepState { return steps[num - 1]; }

  // ── Click-outside dropdowns ────────────────────────────────────────────────

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (projDropRef.current && !projDropRef.current.contains(e.target as Node)) setProjDropOpen(false);
      if (layoutDropRef.current && !layoutDropRef.current.contains(e.target as Node)) setLayoutDropOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Cleanup on unmount ────────────────────────────────────────────────────

  useEffect(() => () => {
    if (prereqPollRef.current) clearInterval(prereqPollRef.current);
    if (genPollRef.current) clearInterval(genPollRef.current);
  }, []);

  // Keep refs in sync with state/context so setInterval callbacks always have fresh values
  useEffect(() => { prereqRef.current = prereqItems; }, [prereqItems]);
  useEffect(() => { authTokenRef.current = authToken ?? ''; }, [authToken]);
  useEffect(() => { pileBinsRef.current = pileBins; }, [pileBins]);

  // authToken persists across reloads (localStorage) but userInfo doesn't. If a
  // token is already present on mount (e.g. carried over from the API Demo page),
  // Step 1's own "Authorize" button never gets clicked, so validate it here and
  // auto-complete Step 1 / unlock Step 2 instead of leaving the wizard stuck.
  useEffect(() => {
    if (!authToken) return;
    if (userInfo) {
      updateStep(1, { status: 'success' });
      unlockStep(2);
      return;
    }
    applyAuthToken(authToken).then(ok => {
      if (ok) {
        updateStep(1, { status: 'success' });
        unlockStep(2);
      }
    });
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fetch projects when authToken is present ─────────────────────────

  useEffect(() => {
    if (!authToken) return;
    setProjectsList([]);
    setProjectsLoading(true);
    fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/my-projects`, {
      headers: { Authorization: `Bearer ${authToken}`, Accept: 'application/json' },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d: any) => setProjectsList(Array.isArray(d) ? d : (d.results ?? [])))
      .catch(err => console.error('[ReportDemo] projects fetch failed:', err))
      .finally(() => setProjectsLoading(false));
  }, [authToken]);

  // ── Auto-select layout from ?layoutId= query param ───────────────────────

  useEffect(() => {
    const paramId = searchParams.get('layoutId');
    if (!paramId || layoutsList.length === 0 || selectedLayout) return;
    const found = layoutsList.find(l => l.id === parseInt(paramId, 10));
    if (found) handleLayoutSelect(found);
  }, [layoutsList]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 2: Cascade actions ───────────────────────────────────────────────

  async function handleProjectSelect(project: Project) {
    setSelectedProject(project);
    setProjDropOpen(false);
    setProjectSearch('');
    setSelectedLayout(null);
    setLayoutsList([]);
    setConfigsList([]);
    setSelectedConfigIds(new Set());
    setLayoutsLoading(true);
    try {
      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/?project_id=${project.id}`,
        { headers: { Authorization: `Bearer ${authToken}`, Accept: 'application/json' } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setLayoutsList(Array.isArray(d) ? d : (d.results ?? []));
    } catch (err) { console.error('[ReportDemo] layouts fetch failed:', err); }
    finally { setLayoutsLoading(false); }
  }

  async function handleLayoutSelect(layout: Layout) {
    setSelectedLayout(layout);
    setLayoutDropOpen(false);
    setConfigsList([]);
    setSelectedConfigIds(new Set());
    setConfigDetailsById({});
    setConfigsLoading(true);

    // Fetch input values (minPileReveal, revealWindow, maxSlopeChange,
    // maxTTAxisSlope, totalGrading) up front so Step 2 / Step 3 can display
    // them — reuses the same endpoint generateReport() calls at submit time.
    if (selectedProject) {
      fetchLayoutConfigurationsDetail(selectedProject.id, layout.id).then(({ configsById, sourceUnit, selectedUnit }) => {
        const enriched: Record<number, Record<string, any>> = {};
        Object.keys(configsById).forEach(idStr => {
          const cfg = configsById[Number(idStr)];
          enriched[cfg.id] = {
            ...cfg,
            earthWorkData: convertEarthworkData(cfg.defaultEarthWorkData, sourceUnit, selectedUnit),
            displayUnit: selectedUnit,
          };
        });
        setConfigDetailsById(enriched);
      });
    }

    try {
      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/configurations/?layout_id=${layout.id}`,
        { headers: { Authorization: `Bearer ${authToken}`, Accept: 'application/json' } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const all: RunConfig[] = Array.isArray(d) ? d : (d.results ?? []);
      // Show all completed configurations — selection itself (not the list) is
      // capped at 9 in toggleConfig().
      setConfigsList(all.filter(c => c.status?.toLowerCase() === 'completed'));
    } catch (err) { console.error('[ReportDemo] configurations fetch failed:', err); }
    finally { setConfigsLoading(false); }
  }

  function toggleConfig(id: number) {
    setSelectedConfigIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else if (next.size < 9) { next.add(id); }
      return next;
    });
  }

  // Pre-fills Input Files / Revision Details from the layout's last generated
  // report (NextrackerTerrainproReportInformation) — mirrors
  // AdvancedReportWizardComponent's addFile()/addRevision(), which seed the
  // form from this.reportInformation.input_info/revision_info before appending
  // a blank row. Without this, the Configure Report step always starts blank
  // even if a report was already generated for this layout before.
  async function fetchPreviousReportInformation(projectId: number, layoutId: number) {
    try {
      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/reports/project/${projectId}/layout/${layoutId}/report-information`,
        { headers: { Authorization: `Bearer ${authTokenRef.current}`, Accept: 'application/json' } }
      );
      if (!r.ok) return;
      const d = await r.json();
      if (Array.isArray(d.input_info) && d.input_info.length > 0) {
        setInputFiles(d.input_info.map((f: any) => ({
          filename: f.filename ?? '', format: f.format ?? '', dated: f.dated ?? '',
        })));
      }
      if (Array.isArray(d.revision_info) && d.revision_info.length > 0) {
        setRevisions(d.revision_info.map((rev: any) => ({
          rev: rev.rev ?? '', date: rev.date ?? '', description: rev.description ?? '',
          preparedBy: rev.preparedBy ?? '', checkedBy: rev.checkedBy ?? '', approvedBy: rev.approvedBy ?? '',
        })));
      }
      // Saved report values take priority over project/layout defaults —
      // mirrors reviewFormGroup's `this.reportInformation?.X || layoutInfo?.X`.
      // Set unconditionally (not guarded by "if empty") so this wins regardless
      // of whether confirmSelection()'s project-default fill already ran.
      if (d.projectName) setProjName(d.projectName);
      if (d.location) setProjLocation(d.location);
      if (d.coordinateSystem) setCoordSys(d.coordinateSystem);
      // Stored latitude/longitude are DMS strings from the last save, not
      // decimals — normalize back to decimal so state stays compatible with
      // generateReport()'s parseFloat(latitude) + toDMS() re-conversion.
      if (d.latitude != null && d.latitude !== '' && d.latitude !== 'NA') {
        const { decimal } = parseLatLng(d.latitude, true);
        if (!isNaN(decimal)) setLatitude(String(decimal));
      }
      if (d.longitude != null && d.longitude !== '' && d.longitude !== 'NA') {
        const { decimal } = parseLatLng(d.longitude, false);
        if (!isNaN(decimal)) setLongitude(String(decimal));
      }
    } catch (err) { console.error('[ReportDemo] report-information fetch failed:', err); }
  }

  function confirmSelection() {
    if (!selectedProject || !selectedLayout || selectedConfigIds.size === 0) return;
    // Pre-populate report metadata from the selected project
    if (selectedProject.project_name) setProjName(selectedProject.project_name);
    if (selectedProject.latitude != null) setLatitude(String(selectedProject.latitude));
    if (selectedProject.longitude != null) setLongitude(String(selectedProject.longitude));
    const loc = [selectedProject.city, selectedProject.state].filter(Boolean).join(', ');
    if (loc) setProjLocation(loc);
    fetchPreviousReportInformation(selectedProject.id, selectedLayout.id);
    updateStep(2, { status: 'success', endTime: new Date() });
    unlockStep(3);
  }

  // ── Step 3: Figure selection ──────────────────────────────────────────────

  function toggleFigureConfig(id: number) {
    setSelectedFigureConfigIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else if (next.size < MAX_FIGURE_CONFIGS) { next.add(id); }
      return next;
    });
  }

  function confirmFigureSelection() {
    updateStep(3, { status: 'success', endTime: new Date() });
    unlockStep(4);
  }

  // ── Step 4: Prerequisites ─────────────────────────────────────────────────

  // Single status endpoint used by both initial check and polling.
  // GET /tools/api/v1/terrain-pro/reports/project/{projectId}/layout/{layoutId}/heatmap-status
  // Response: { configurations: [{ id, heatmapExists, pileRevealExists, totalGrading }] }
  // token is passed explicitly so callers control whether they use the live context value
  // (click-handler path) or the always-current ref (setInterval path).
  // get_geojson_data only includes `bounds` once the layer's terrain data is
  // already stored (layer_status truthy) — i.e. once heatmapExists is true, not
  // at the moment a fresh generation is first triggered. So this is called from
  // checkHeatmapStatus (which always runs) rather than triggerHeatmap (which
  // only runs when a heatmap doesn't already exist — the case where bounds is
  // least likely to be available yet).
  async function fetchTilesBounds(configId: number): Promise<void> {
    if (tilesBoundsRef.current || !selectedProject || !selectedLayout) return;
    try {
      const body = new URLSearchParams();
      body.append('epsg', epsgRef.current);
      body.append('configuration_id', String(configId));
      body.append('pitch', '0');
      body.append('layout_id', String(selectedLayout.id));
      body.append('project_id', String(selectedProject.id));
      body.append('socket_id', `report_demo_bounds_${configId}`);
      const r = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/reports/tiles`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authTokenRef.current}`, Accept: 'application/json' },
        body,
      });
      if (r.ok) {
        const d = await r.json();
        // get_geojson_data nests the actual bounds one level deeper — matches
        // generateAdvancedReportDocx's tilesResponse?.bounds?.bounds.
        if (d?.bounds?.bounds) tilesBoundsRef.current = d.bounds.bounds;
      }
    } catch (err) { console.error('[ReportDemo] fetchTilesBounds failed:', err); }
  }

  async function checkHeatmapStatus(token: string): Promise<void> {
    if (!selectedProject || !selectedLayout || !token) return;
    const requestInfo = {
      method: 'GET',
      url: `/tools/api/v1/terrain-pro/reports/project/${selectedProject.id}/layout/${selectedLayout.id}/heatmap-status`,
      project_id: selectedProject.id,
      layout_id: selectedLayout.id,
    };
    try {
      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/reports/project/${selectedProject.id}/layout/${selectedLayout.id}/heatmap-status`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
      );
      if (!r.ok) {
        console.error(`[ReportDemo] checkHeatmapStatus HTTP ${r.status}`);
        setHeatmapStatusApiLog({ request: requestInfo, response: { status: r.status, statusText: r.statusText } });
        return;
      }
      const d = await r.json();
      setHeatmapStatusApiLog({ request: requestInfo, response: d });
      const configs: Array<{ id: number; heatmapExists: boolean; pileRevealExists: boolean }> = d.configurations ?? [];
      let changed = false;
      configs.forEach(cfg => {
        const cur = prereqRef.current[cfg.id];
        if (!cur) return;
        if (cfg.heatmapExists) fetchTilesBounds(cfg.id);
        const heatmapStatus: PrereqItem['heatmapStatus'] =
          cfg.heatmapExists ? 'completed' : cur.heatmapStatus;
        const pileRevealStatus: PrereqItem['pileRevealStatus'] =
          cfg.pileRevealExists ? 'completed' : cur.pileRevealStatus;
        if (heatmapStatus !== cur.heatmapStatus || pileRevealStatus !== cur.pileRevealStatus) {
          prereqRef.current[cfg.id] = { ...cur, heatmapStatus, pileRevealStatus };
          changed = true;
        }
      });
      if (changed) setPrereqItems({ ...prereqRef.current });
    } catch (err) {
      console.error('[ReportDemo] checkHeatmapStatus failed:', err);
    }
  }

  // POST /tools/api/v1/terrain-pro/reports/tiles — triggers heatmap (vector tile) generation; needs EPSG from project detail.
  // NOTE: view uses request.POST.get() so payload must be form-encoded, not JSON.
  async function triggerHeatmap(token: string, configId: number): Promise<void> {
    if (!selectedProject || !selectedLayout) return;
    try {
      const body = new URLSearchParams();
      body.append('epsg', epsgRef.current);
      body.append('configuration_id', String(configId));
      body.append('pitch', '0');
      body.append('layout_id', String(selectedLayout.id));
      body.append('project_id', String(selectedProject.id));
      body.append('socket_id', `report_demo_heatmap_${configId}`);

      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/reports/tiles`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          body,
        }
      );
      // Mirrors production's tilesResponse?.bounds?.bounds — captured here since
      // this is the only bounds-producing call this demo already makes.
      try {
        const d = await r.clone().json();
        if (d?.bounds?.bounds) tilesBoundsRef.current = d.bounds.bounds;
      } catch { /* response may not be JSON (e.g. error body) */ }
      prereqRef.current[configId] = { ...prereqRef.current[configId], heatmapStatus: 'running' };
      setPrereqItems({ ...prereqRef.current });
    } catch (err) {
      console.error('[ReportDemo] triggerHeatmap failed:', err);
      prereqRef.current[configId] = { ...prereqRef.current[configId], heatmapStatus: 'error' };
      setPrereqItems({ ...prereqRef.current });
    }
  }

  // POST /tools/api/v1/terrain-pro/reports/generate-pile-reveal — triggers pile reveal layer.
  // Response: { success, ready?, status?, s3_path? } — ready:true or status:"complete" means already done.
  // NOTE: view uses request.POST.get() so payload must be form-encoded, not JSON.
  async function triggerPileReveal(token: string, configId: number): Promise<void> {
    if (!selectedProject || !selectedLayout) return;
    const requestBody = {
      configuration_id: String(configId),
      layout_id: String(selectedLayout.id),
      project_id: String(selectedProject.id),
      socket_id: `pile_reveal_layer_${configId}`,
      bounds: '{}',
    };
    // Clear the previous entry for this config before the new call resolves,
    // so a stale response is never shown next to a newer request.
    setPileRevealApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response: null } }));
    try {
      const body = new URLSearchParams();
      body.append('configuration_id', requestBody.configuration_id);
      body.append('layout_id', requestBody.layout_id);
      body.append('project_id', requestBody.project_id);
      body.append('socket_id', requestBody.socket_id);
      body.append('bounds', requestBody.bounds);

      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/reports/generate-pile-reveal`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
          body,
        }
      );
      let pileRevealStatus: PrereqItem['pileRevealStatus'];
      let responseData: any;
      if (r.status === 404 || r.status === 405) {
        pileRevealStatus = 'not_available';
        responseData = { status: r.status, statusText: r.statusText };
      } else if (r.ok) {
        const d = await r.json();
        responseData = d;
        pileRevealStatus = (d.ready === true || d.status === 'complete') ? 'completed' : 'running';
      } else {
        pileRevealStatus = 'not_available';
        responseData = { status: r.status, statusText: r.statusText };
      }
      setPileRevealApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response: responseData } }));
      prereqRef.current[configId] = { ...prereqRef.current[configId], pileRevealStatus };
      setPrereqItems({ ...prereqRef.current });
    } catch (err: any) {
      console.error('[ReportDemo] triggerPileReveal failed:', err);
      setPileRevealApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response: { error: err?.message ?? String(err) } } }));
      prereqRef.current[configId] = { ...prereqRef.current[configId], pileRevealStatus: 'not_available' };
      setPrereqItems({ ...prereqRef.current });
    }
  }

  async function startPrereqs() {
    // Capture the live authToken at click time — guaranteed non-empty here since
    // Step 1 (Authorize) gates the whole wizard, so auth is guaranteed non-empty here.
    const token = authToken;
    authTokenRef.current = token;         // also sync the ref for the upcoming poll

    updateStep(4, { status: 'running', startTime: new Date() });

    // Build initial state (all checking) — scoped to figure-selected configs only,
    // since prerequisites (heatmap + pile reveal) only matter for the configs that
    // will actually get a figure embedded in the report.
    const initial: Record<number, PrereqItem> = {};
    const configIdArr: number[] = [];
    selectedFigureConfigIds.forEach(id => {
      configIdArr.push(id);
      const cfg = configsList.find(c => c.id === id);
      initial[id] = {
        configId: id,
        configName: cfg?.configuration_name ?? `Config ${id}`,
        heatmapStatus: 'checking',
        pileRevealStatus: 'checking',
      };
    });
    setPrereqItems(initial);
    prereqRef.current = initial;

    // Fetch EPSG from project detail — needed for heatmap tile trigger
    if (selectedProject) {
      try {
        const pr = await fetch(
          `${API_BASE_URL}/tools/api/v1/terrain-pro/projects/${selectedProject.id}/`,
          { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
        );
        if (pr.ok) {
          const pd = await pr.json();
          epsgRef.current = String(pd.epsg_code ?? '4326');
          // Backfill any metadata fields not already set from the project list
          if (pd.project_name && !projName) setProjName(pd.project_name);
          if (pd.latitude != null && !latitude) setLatitude(String(pd.latitude));
          if (pd.longitude != null && !longitude) setLongitude(String(pd.longitude));
          const loc = [pd.city, pd.state].filter(Boolean).join(', ');
          if (loc && !projLocation) setProjLocation(loc);
          // Resolve + reflect the CRS name into the UI now (matching
          // generateAdvancedReportDocx's resolvedCoordinateSystem) — previously
          // this only happened inside generateReport()'s local variable, so the
          // Configure Report form field never showed it.
          if (!coordSys) {
            resolveCoordinateSystem().then(resolved => {
              if (resolved && resolved !== 'N/A') setCoordSys(resolved);
            });
          }
        }
      } catch { /* fall back to 4326 */ }
    }

    // Check which layers already exist
    await checkHeatmapStatus(token);

    // Trigger any missing layers in parallel
    await Promise.all(configIdArr.map(async id => {
      const item = prereqRef.current[id];
      if (!item) return;
      if (item.heatmapStatus !== 'completed') await triggerHeatmap(token, id);
      if (item.pileRevealStatus !== 'completed') await triggerPileReveal(token, id);
    }));

    // If everything was already ready, complete immediately
    const allReady = Object.values(prereqRef.current).every(
      item => item.heatmapStatus === 'completed' &&
              (item.pileRevealStatus === 'completed' || item.pileRevealStatus === 'not_available')
    );
    if (allReady) {
      updateStep(4, { status: 'success', endTime: new Date() });
      unlockStep(5);
      return;
    }

    prereqPollRef.current = setInterval(pollPrereqsOnce, 5000);
  }

  async function pollPrereqsOnce() {
    // authTokenRef.current is always current (kept in sync by useEffect)
    await checkHeatmapStatus(authTokenRef.current);

    const allDone = Object.values(prereqRef.current).every(
      item => item.heatmapStatus === 'completed' &&
              (item.pileRevealStatus === 'completed' || item.pileRevealStatus === 'not_available')
    );
    const anyError = Object.values(prereqRef.current).some(item => item.heatmapStatus === 'error');

    if (allDone) {
      if (prereqPollRef.current) clearInterval(prereqPollRef.current);
      updateStep(4, { status: 'success', endTime: new Date() });
      unlockStep(5);
    } else if (anyError) {
      if (prereqPollRef.current) clearInterval(prereqPollRef.current);
      updateStep(4, { status: 'error', errorMessage: 'Heatmap layer generation failed for one or more configurations.' });
    }
  }

  function retryPrereqs() {
    setPrereqItems({});
    prereqRef.current = {};
    setHeatmapStatusApiLog(null);
    setPileRevealApiLog({});
    startPrereqs();
  }

  // ── Step 5: Pile Binning ──────────────────────────────────────────────────

  function autoSavePileBins(configId: number, entry: PileBinningEntry) {
    const analysis = entry.analysis;
    const binsMap   = analysis[0]?.imported_data   ?? {};
    const totals    = analysis[1]?.imported_totals  ?? {};
    const chartData = analysis[2]?.imported_chart_data ?? [];
    const requestBody = {
      config_id: String(configId),
      guid: entry.guid,
      data: { binsmap: binsMap, totalvalues: totals, chartdata: chartData, source: 'imported' },
      data_source: 'imported',
    };
    // Clear the previous entry for this config before the new call resolves,
    // so a stale response is never shown next to a newer request.
    setAutoSaveApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response: null } }));
    fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/reports/pile-binning/auto_save`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authTokenRef.current}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
      .then(async r => {
        const response = await r.json().catch(() => ({ status: r.status, statusText: r.statusText }));
        setAutoSaveApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response } }));
      })
      .catch(err => {
        console.error('[ReportDemo] pile binning auto-save failed:', err);
        setAutoSaveApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response: { error: err.message ?? String(err) } } }));
      });
  }

  async function loadPileBinningForConfig(configId: number) {
    if (pileBins[configId] || pileBinsLoading[configId]) return;
    setPileBinsLoading(prev => ({ ...prev, [configId]: true }));
    setPileBinsError(prev => ({ ...prev, [configId]: null }));
    const guid = genGuid();
    const requestBody = {
      configId: String(configId),
      guid,
      connection_id: '0',
      binsmap: null,
      unit: 'ft',
      reqSource: 'TP',
      file_name: `Terrain_pro_${configId}`,
    };
    setPileBinningApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response: null } }));
    try {
      const r = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/reports/get-pile-binning-data`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(requestBody),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setPileBinningApiLog(prev => ({ ...prev, [configId]: { request: requestBody, response: d } }));
      setPileBins(prev => ({
        ...prev,
        [configId]: { guid, unit: d.unit ?? 'ft', source: d.source ?? '', analysis: d.analysis ?? [] },
      }));
    } catch (err: any) {
      setPileBinsError(prev => ({ ...prev, [configId]: err.message ?? 'Failed to load pile binning data.' }));
    } finally {
      setPileBinsLoading(prev => ({ ...prev, [configId]: false }));
    }
  }

  useEffect(() => {
    if (getStep(5).status !== 'ready' && getStep(5).status !== 'running') return;
    // Scoped to figure-selected configs only — pile binning tables are only
    // rendered as figures for those, not every originally selected config.
    selectedFigureConfigIds.forEach(configId => { loadPileBinningForConfig(configId); });
  }, [steps[4].status]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleBinColorChange(configId: number, pileKey: string, binIdx: number, color: string) {
    setPileBins(prev => {
      const entry = prev[configId];
      if (!entry) return prev;
      const newAnalysis = entry.analysis.map((a, i) => {
        if (i !== 0) return a;
        const binsMap = { ...a.imported_data };
        const arr = [...(binsMap[pileKey] ?? [])];
        arr[binIdx] = { ...arr[binIdx], color };
        binsMap[pileKey] = arr;
        return { ...a, imported_data: binsMap };
      });
      const updated = { ...entry, analysis: newAnalysis };
      autoSavePileBins(configId, updated);
      return { ...prev, [configId]: updated };
    });
  }

  async function handleSplitBin(configId: number, pileKey: string, binIdx: number) {
    const entry = pileBins[configId];
    if (!entry) return;
    const bin: PileBin = entry.analysis[0]?.imported_data?.[pileKey]?.[binIdx];
    if (!bin) return;
    const val = parseFloat(splitInputVal);
    if (isNaN(val) || val <= bin.start || val >= bin.end) return;

    const payload = {
      pile_name: pileKey,
      range_value: String(val),
      reveal_range: [bin.start, bin.end],
      color: bin.color ?? '#4895EF',
      symbol: 'basic-circle',
      category: bin.category ?? '',
      label: bin.name ?? '',
      section_type: bin.section_type ?? '',
      minimum_embedment: bin.minimum_embedment ?? 0,
      guid: entry.guid,
      config_id: configId,
      tpSource: entry.source,
      unit: entry.unit,
      data_source: 'imported',
      reveal_range_value: 0.250,
    };
    // Clear the previous entry for this config before the new call resolves,
    // so a stale response is never shown next to a newer request.
    setSplitApiLog(prev => ({ ...prev, [configId]: { request: payload, response: null } }));
    try {
      const r = await fetch(`${API_BASE_URL}/tools/api/v1/pile-binning/split_range`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload),
      });
      const result = await r.json();
      setSplitApiLog(prev => ({ ...prev, [configId]: { request: payload, response: result } }));
      setPileBins(prev => {
        const e = prev[configId];
        if (!e) return prev;
        const analysis = e.analysis;
        const binsMap = { ...(analysis[0]?.imported_data ?? {}) };
        const pileArr = [...(binsMap[pileKey] ?? [])];
        let newBinRows: any[];
        let newChartRows: any[];
        let newTotals: any;
        if (result?.analysis) {
          newBinRows  = result.analysis[0]?.imported_data ?? [];
          newChartRows = result.analysis[2]?.imported_chart_data ?? [];
          newTotals   = result.analysis[1]?.imported_totals;
        } else {
          newBinRows  = result[0] ?? [];
          newChartRows = result[1] ?? [];
          newTotals   = analysis[1]?.imported_totals;
        }
        binsMap[pileKey] = [...pileArr.slice(0, binIdx), ...newBinRows, ...pileArr.slice(binIdx + 1)];
        const existingChart: any[] = analysis[2]?.imported_chart_data ?? [];
        const updatedAnalysis = [
          { ...(analysis[0] ?? {}), imported_data: binsMap },
          { ...(analysis[1] ?? {}), imported_totals: newTotals ?? analysis[1]?.imported_totals },
          { ...(analysis[2] ?? {}), imported_chart_data: [
            ...existingChart.filter((c: any) =>
              c.pile_name !== pileKey || c.reveal_range[1] <= bin.start || c.reveal_range[0] >= bin.end
            ),
            ...newChartRows,
          ]},
        ];
        const updated = { ...e, analysis: updatedAnalysis };
        autoSavePileBins(configId, updated);
        return { ...prev, [configId]: updated };
      });
      setSplitEditing(null);
      setSplitInputVal('');
    } catch (err: any) {
      console.error('[ReportDemo] split failed:', err);
      setSplitApiLog(prev => ({ ...prev, [configId]: { request: payload, response: { error: err.message ?? String(err) } } }));
    }
  }

  async function handleDeleteBin(configId: number, pileKey: string, binIdx: number) {
    const entry = pileBins[configId];
    if (!entry) return;
    const pileArr: PileBin[] = entry.analysis[0]?.imported_data?.[pileKey] ?? [];
    if (pileArr.length <= 1 || binIdx === 0) return;

    const payload = {
      guid: entry.guid,
      pile_name: pileKey,
      pile_list: pileArr,
      config_id: configId,
      tpSource: entry.source,
      unit: entry.unit,
      current_bin_index: binIdx,
      reveal_range_value: 0.250,
      data_source: 'imported',
    };
    try {
      const r = await fetch(`${API_BASE_URL}/tools/api/v1/pile-binning/delete_range`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload),
      });
      const result = await r.json();
      setPileBins(prev => {
        const e = prev[configId];
        if (!e) return prev;
        const analysis = e.analysis;
        const binsMap = { ...(analysis[0]?.imported_data ?? {}) };
        let newBins: any[];
        let newChart: any[];
        let newTotals: any;
        if (result?.analysis) {
          newBins  = result.legacy_result?.[0] ?? [];
          newChart = result.legacy_result?.[1] ?? [];
          newTotals = result.analysis[1]?.imported_totals;
        } else {
          newBins  = result[0] ?? [];
          newChart = result[1] ?? [];
          newTotals = analysis[1]?.imported_totals;
        }
        if (newBins.length) binsMap[pileKey] = newBins;
        const existingChart: any[] = analysis[2]?.imported_chart_data ?? [];
        const updatedChart = newChart.length
          ? [...existingChart.filter((c: any) => c.pile_name !== pileKey), ...newChart]
          : existingChart;
        const updatedAnalysis = [
          { ...(analysis[0] ?? {}), imported_data: binsMap },
          { ...(analysis[1] ?? {}), imported_totals: newTotals ?? analysis[1]?.imported_totals },
          { ...(analysis[2] ?? {}), imported_chart_data: updatedChart },
        ];
        const updated = { ...e, analysis: updatedAnalysis };
        autoSavePileBins(configId, updated);
        return { ...prev, [configId]: updated };
      });
    } catch (err) {
      console.error('[ReportDemo] delete failed:', err);
    }
  }

  function confirmPileBinning() {
    updateStep(5, { status: 'success', endTime: new Date() });
    unlockStep(6);
  }

  // ── Step 6: Config form ───────────────────────────────────────────────────

  function confirmReportConfig() {
    updateStep(6, { status: 'success', endTime: new Date() });
    unlockStep(7);
  }

  // Input files helpers
  const addInputFile  = () => setInputFiles(prev => [...prev, { filename: '', format: '', dated: new Date().toISOString().slice(0, 10) }]);
  const delInputFile  = (i: number) => setInputFiles(prev => prev.filter((_, x) => x !== i));
  const editInputFile = (i: number, k: keyof InputFile, v: string) =>
    setInputFiles(prev => prev.map((f, x) => x === i ? { ...f, [k]: v } : f));

  // Revision helpers
  const addRevision  = () => setRevisions(prev => [...prev, { rev: '', date: '', description: '', preparedBy: '', checkedBy: '', approvedBy: '' }]);
  const delRevision  = (i: number) => setRevisions(prev => prev.filter((_, x) => x !== i));
  const editRevision = (i: number, k: keyof Revision, v: string) =>
    setRevisions(prev => prev.map((r, x) => x === i ? { ...r, [k]: v } : r));

  // ── Step 7: Generate report ───────────────────────────────────────────────

  // Single source of truth for both layoutInfo (layoutName) and per-config
  // earthWorkData — mirrors what the production Angular analysis tab loads via
  // getTerrainProConfigurations() before calling generateAdvancedReportDocx().
  async function fetchLayoutConfigurationsDetail(projectId: number, layoutId: number): Promise<{
    layoutInfo: Record<string, any>;
    configsById: Record<number, Record<string, any>>;
    sourceUnit: UnitSystem;
    selectedUnit: UnitSystem;
  }> {
    try {
      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/reports/project/${projectId}/layout/${layoutId}/configurations`,
        { headers: { Authorization: `Bearer ${authTokenRef.current}`, Accept: 'application/json' } }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      // sourceUnit = the layout's storage convention (TerrainProLayouts.source_unit).
      // selectedUnit = the display preference (unitSelection.length — an object
      // shaped like { length: 'SI'|'Imperial', angle, isDefaultUpdated }, matching
      // this.selectedUnit in the Angular app).
      const sourceUnit: UnitSystem = d.sourceUnit === 'Imperial' ? 'Imperial' : 'SI';
      const selectedUnit: UnitSystem = d.unitSelection?.length === 'Imperial' ? 'Imperial' : 'SI';
      const configsById: Record<number, Record<string, any>> = {};
      (d.configurations ?? []).forEach((c: any) => {
        // algorithmVersionNumber < 6 runs never got separate North/South slope
        // fields populated — fall back to the single combined maxTTAxisSlope.
        const hasSplitSlope = c.algorithmVersionNumber >= 6;
        configsById[c.id] = {
          id: c.id,
          name: c.configurationName,
          minPileReveal: c.minPileReveal,
          maxTTAxisSlope: c.maxTTAxisSlope,
          maxTTAxisSlopeNorth: hasSplitSlope ? c.maxTTAxisSlopeNorth : c.maxTTAxisSlope,
          maxTTAxisSlopeSouth: hasSplitSlope ? c.maxTTAxisSlopeSouth : c.maxTTAxisSlope,
          // Raw storage is in meters (SI) / feet (Imperial); display convention
          // is mm/inches — matches formConfigTableData()'s revealWindow * 1000/12.
          revealWindow: sourceUnit === 'SI' ? c.revealWindow * 1000 : c.revealWindow * 12,
          maxSlopeChange: c.maxSlopeChange,
          isBookmarked: c.isBookMarked,
          isCompleted: c.isCompleted,
          sortingSequence: c.sortingSequence,
          slopeAdjustment: c.slopeAdjustment,
          optimizationGoal: c.optimizationGoal,
          status: c.status,
          unit: { length: sourceUnit, angle: 'Degree', isDefaultUpdated: false },
          // Raw, unconverted — matches production's config.defaultEarthWorkData.
          // The unit-converted config.earthWorkData is derived per-report in
          // generateReport(), once sourceUnit/selectedUnit are known.
          defaultEarthWorkData: c.earthWorkData ?? {},
        };
      });
      return { layoutInfo: d.layoutInfo ?? {}, configsById, sourceUnit, selectedUnit };
    } catch (err) {
      console.error('[ReportDemo] layout configurations detail fetch failed:', err);
      return { layoutInfo: {}, configsById: {}, sourceUnit: 'SI', selectedUnit: 'SI' };
    }
  }

  // Ports home-page.component.ts's convertLegendValueToSelectedUnit — converts a
  // value stored in sourceUnit's linear unit into selectedUnit's, if they differ.
  function convertLegendValueToSelectedUnit(value: number, sourceUnit: UnitSystem, selectedUnit: UnitSystem): number {
    if (sourceUnit === selectedUnit) return value;
    return sourceUnit === 'SI'
      ? parseFloat((value * 3.28084).toFixed(2))   // m -> ft
      : parseFloat((value / 3.28084).toFixed(2));  // ft -> m
  }

  // Mirrors generateAdvancedReportDocx's resolvedCoordinateSystem: use the
  // user-entered value if present, else look up the EPSG code's CRS name.
  async function resolveCoordinateSystem(): Promise<string> {
    if (coordSys) return coordSys;
    if (!epsgRef.current) return 'N/A';
    try {
      const r = await fetch(
        `${API_BASE_URL}/tools/api/v1/terrain-pro/reports/epsg-data?coord_ref_sys_code=${epsgRef.current}`,
        { headers: { Authorization: `Bearer ${authTokenRef.current}`, Accept: 'application/json' } }
      );
      if (r.ok) {
        const d = await r.json();
        const row = Array.isArray(d) ? d[0] : d;
        if (row?.coord_ref_sys_name) return row.coord_ref_sys_name;
      }
    } catch (err) { console.error('[ReportDemo] epsg-data fetch failed:', err); }
    return 'N/A';
  }

  async function generateReport() {
    if (!selectedLayout || !selectedProject) return;
    if (genPollRef.current) { clearInterval(genPollRef.current); genPollRef.current = null; }
    updateStep(7, { status: 'running', startTime: new Date(), errorMessage: null });
    setReportStatusMsg('Submitting report generation request…');

    const filledFiles = inputFiles.filter(f => f.filename || f.format || f.dated);
    const filledRevisions = revisions.filter(r => r.rev || r.description);

    // All originally selected configs feed the earthwork/model-parameter tables;
    // only the figure-selected subset gets a heatmap + pile-binning image.
    // sourceUnit = layout's storage convention; selectedUnit = display preference
    // (TerrainProLayouts.unit_selection / TerrainProUserSettings) — these can differ.
    const { layoutInfo, configsById, sourceUnit, selectedUnit } =
      await fetchLayoutConfigurationsDetail(selectedProject.id, selectedLayout.id);
    const selectedConfigDetails = Array.from(selectedConfigIds).map(id => {
      const cfg = configsById[id] ?? { id, name: configsList.find(c => c.id === id)?.configuration_name ?? `Config ${id}` };
      return { ...cfg, earthWorkData: convertEarthworkData(cfg.defaultEarthWorkData, sourceUnit, selectedUnit) };
    });

    // totalArrayArea is a layout-wide constant duplicated onto every config's
    // earthWorkData.area — any config with it populated works. Read from the
    // converted earthWorkData (not defaultEarthWorkData), matching
    // this.selectedConfig['earthWorkData']['area']['totalArrayArea'].
    let arrayArea = '';
    for (const cfg of selectedConfigDetails) {
      const total = cfg?.earthWorkData?.area?.totalArrayArea;
      if (typeof total === 'number') { arrayArea = total.toFixed(1); break; }
    }

    // PileRevealLegendRanges — derived from the first figure-selected config's
    // minPileReveal/revealWindow, converted to a base linear unit (m or ft) using
    // sourceUnit (revealWindow is stored in the layout's native mm/inch convention
    // from when the run was submitted, not the later-changeable display unit),
    // then converted to selectedUnit and split into 5 descending bins.
    let pileRevealLegendRanges: Array<{ start: number; end: number }> = [];
    // filteredConfigs = selectedConfigurations.filter(...) iterates configTableData's
    // own order (sorted by sortingSequence), not filteredConfigurationIds' order —
    // so refConfig is the sortingSequence-first figure-selected config, not
    // whichever one was checked first in the UI.
    const refConfig = Object.values(configsById)
      .filter(c => selectedFigureConfigIds.has(c.id))
      .sort((a, b) => (a.sortingSequence ?? 0) - (b.sortingSequence ?? 0))[0];
    if (refConfig?.minPileReveal !== undefined && refConfig?.revealWindow !== undefined) {
      const revealWindowBase = sourceUnit === 'SI'
        ? (refConfig.revealWindow !== 0 ? refConfig.revealWindow / 1000 : refConfig.revealWindow)
        : refConfig.revealWindow / 12;
      const rangeMin = convertLegendValueToSelectedUnit(refConfig.minPileReveal, sourceUnit, selectedUnit);
      const rangeMax = convertLegendValueToSelectedUnit(refConfig.minPileReveal + revealWindowBase, sourceUnit, selectedUnit);
      pileRevealLegendRanges = generateDescendingLegendRanges(rangeMin, rangeMax, 5);
    }

    const latNum = parseFloat(latitude);
    const lonNum = parseFloat(longitude);
    const latDMS = !isNaN(latNum) ? toDMS(latNum, true) : (latitude || '');
    const lonDMS = !isNaN(lonNum) ? toDMS(lonNum, false) : (longitude || '');
    const resolvedCoordinateSystem = await resolveCoordinateSystem();

    const body: Record<string, any> = {
      projectId: selectedProject.id,
      layoutId: selectedLayout.id,
      projectName: projName || selectedProject.project_name || '',
      location: projLocation || '',
      latitude: latDMS,
      longitude: lonDMS,
      lat: !isNaN(latNum) ? latNum : null,
      lon: !isNaN(lonNum) ? lonNum : null,
      layoutName: layoutInfo?.layoutName || selectedLayout.name || 'Layout',
      coordinateSystem: resolvedCoordinateSystem,
      epsgCode: epsgRef.current || 'N/A',
      filteredConfigIds: Array.from(selectedFigureConfigIds),
      selectedConfigDetails,
      units: selectedUnit,
      sourceUnit,
      arrayAreaUnit: selectedUnit === 'SI' ? 'ha' : 'ac',
      arrayArea,
      email: userInfo?.user_email ?? '',
      bounds: tilesBoundsRef.current ?? {},
      PileRevealLegendRanges: pileRevealLegendRanges,
      socket_key: `report_demo_${selectedLayout.id}`,
      revisionDetails: filledRevisions,
      inputFiles: filledFiles,
    };

    // Surface the request payload immediately (while running), matching the
    // API Demo's pattern of showing "View Request Payload" before the response arrives.
    updateStep(7, { requestData: body });

    try {
      // Uses the native new-API report-generation endpoint (not a legacy
      // proxy) — it queues the exact same generate_nextracker_docx_template_task
      // Celery task as the legacy /download-nextracker-advanced-report-docx?version=template
      // route, but resolves projectId/layoutId/email server-side instead of
      // requiring them in the body, so it needs no auth workaround at all.
      const endpoint = `${API_BASE_URL}/tools/api/v1/terrain-pro/layouts/${selectedLayout.id}/reports/nextracker-advanced`;

      const r = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authTokenRef.current}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        let errMsg = `Error ${r.status}`;
        try { const d = await r.json(); errMsg = d.detail ?? d.message ?? d.error ?? errMsg; } catch { /**/ }
        updateStep(7, { status: 'error', errorMessage: errMsg });
        return;
      }

      const d = await r.json();
      const taskId: string = d.task_id ?? d.id;
      if (!taskId) {
        updateStep(7, { status: 'error', errorMessage: 'No task ID returned by server.' });
        return;
      }
      setReportStatusMsg('Generating report… this may take a minute.');

      // Poll for task completion
      let attempts = 0;
      genPollRef.current = setInterval(async () => {
        attempts++;
        if (attempts > 120) {
          clearInterval(genPollRef.current!); genPollRef.current = null;
          updateStep(7, { status: 'error', errorMessage: 'Report generation timed out after 10 minutes.' });
          return;
        }
        try {
          const sr = await fetch(
            `${API_BASE_URL}/tools/api/v1/terrain-pro/reports/nextracker-advanced/${taskId}`,
            { headers: { Authorization: `Bearer ${authTokenRef.current}` } }
          );
          const sd = await sr.json();
          if (sd.status === 'SUCCESS') {
            clearInterval(genPollRef.current!); genPollRef.current = null;
            const fileUrl: string = sd.result?.url ?? '';
            const fileName: string = sd.result?.file_name ?? 'terrain_pro_report.docx';
            setDownloadUrl(fileUrl);
            setDownloadFilename(fileName);
            // Trigger immediate download
            const a = document.createElement('a');
            a.href = fileUrl; a.download = fileName;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            updateStep(7, {
              status: 'success',
              endTime: new Date(),
              requestData: body,
              responseData: { generate_response: d, task_status: sd },
            });
            unlockStep(8);
          } else if (sd.status === 'FAILURE') {
            clearInterval(genPollRef.current!); genPollRef.current = null;
            updateStep(7, { status: 'error', errorMessage: sd.error ?? 'Report generation failed.' });
          }
          // PENDING / STARTED → keep polling
        } catch { /**/ }
      }, 5000);

    } catch (err: any) {
      updateStep(7, { status: 'error', errorMessage: err.message ?? 'Failed to generate report.' });
    }
  }

  // ── Step 8: Re-download ───────────────────────────────────────────────────

  function fetchDownloadUrl() {
    if (!downloadUrl) return;
    updateStep(8, { status: 'running', startTime: new Date(), errorMessage: null });
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = downloadFilename || 'terrain_pro_report.docx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    updateStep(8, { status: 'success', endTime: new Date() });
  }

  // Resets the whole wizard back to Step 1 so the user can generate a report
  // for a different project/layout. Leaves projectsList cached (no need to
  // refetch) — only clears selections and step progress.
  function startAnotherReport() {
    if (genPollRef.current) { clearInterval(genPollRef.current); genPollRef.current = null; }
    if (prereqPollRef.current) { clearInterval(prereqPollRef.current); prereqPollRef.current = null; }

    // Step 1 (Authorize) stays completed — the user's token/session doesn't
    // need re-entering just to generate another report.
    setSteps([
      { stepNumber: 1, status: 'success', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
      { stepNumber: 2, status: 'ready',  requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
      { stepNumber: 3, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
      { stepNumber: 4, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
      { stepNumber: 5, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
      { stepNumber: 6, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
      { stepNumber: 7, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
      { stepNumber: 8, status: 'locked', requestData: {}, responseData: null, errorMessage: null, startTime: null, endTime: null },
    ]);

    // Step 2 — project/layout/configuration selection
    setSelectedProject(null);
    setProjectSearch('');
    setLayoutsList([]);
    setSelectedLayout(null);
    setConfigsList([]);
    setSelectedConfigIds(new Set());
    setConfigDetailsById({});

    // Step 3 — figure selection
    setSelectedFigureConfigIds(new Set());

    // Step 4 — prerequisites
    setPrereqItems({});
    prereqRef.current = {};
    tilesBoundsRef.current = null;

    // Step 5 — pile binning
    setPileBins({});
    pileBinsRef.current = {};
    setPileBinsLoading({});
    setPileBinsError({});
    setActivePileBinTab(0);
    setPileBinningApiLog({});
    setAutoSaveApiLog({});
    setSplitApiLog({});

    // Step 6 — report configuration
    setProjName('');
    setProjLocation('');
    setCoordSys('');
    setLatitude('');
    setLongitude('');
    setInputFiles([{ filename: '', format: '', dated: new Date().toISOString().slice(0, 10) }]);
    setRevisions([]);

    // Step 7/8 — generation + download
    setReportStatusMsg('');
    setDownloadUrl('');
    setDownloadFilename('');

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Render helpers ────────────────────────────────────────────────────────

  function stepCardClass(n: number) {
    const s = getStep(n).status;
    return ['report-step-card',
      s === 'success' ? 'success' : '',
      s === 'error'   ? 'error'   : '',
      (s === 'ready' || s === 'running') ? 'current' : '',
      s === 'locked'  ? 'locked'  : '',
    ].filter(Boolean).join(' ');
  }

  function badgeClass(status: string) {
    const map: Record<string, string> = {
      locked: 'badge-locked', ready: 'badge-ready',
      running: 'badge-running', success: 'badge-success', error: 'badge-error',
    };
    return `report-status-badge ${map[status] ?? 'badge-locked'}`;
  }
  function badgeLabel(status: string) {
    return ({ locked: 'Locked', ready: 'Ready', running: 'Running', success: 'Complete', error: 'Error' } as Record<string, string>)[status] ?? status;
  }

  // Shared input-values line for Step 2 (config selection) and Step 3 (figure
  // selection) — minPileReveal, revealWindow, maxSlopeChange, maxTTAxisSlope,
  // totalGrading, sourced from configDetailsById (fetched in handleLayoutSelect).
  function renderConfigInputSummary(id: number) {
    const d = configDetailsById[id];
    if (!d) return null;
    const isSI = d.unit?.length !== 'Imperial';
    const lengthUnit = isSI ? 'm' : 'ft';
    const revealUnit = isSI ? 'mm' : 'in';
    const volumeUnit = d.displayUnit === 'Imperial' ? 'bcy' : 'bcm';
    const fmt = (v: any, digits = 2) => typeof v === 'number' ? v.toFixed(digits) : '—';
    const totalGrading = d.earthWorkData?.volume?.totalGrading;
    return (
      <div className="config-item-inputs">
        <span>Min pile reveal: <strong>{fmt(d.minPileReveal)} {lengthUnit}</strong></span>
        <span>Reveal window: <strong>{fmt(d.revealWindow, 0)} {revealUnit}</strong></span>
        <span>Max slope change: <strong>{fmt(d.maxSlopeChange)}°</strong></span>
        <span>Max TT axis slope: <strong>{fmt(d.maxTTAxisSlope)}°</strong></span>
        <span>Total grading: <strong>{typeof totalGrading === 'number' ? totalGrading.toFixed(0) : '—'} {volumeUnit}</strong></span>
      </div>
    );
  }

  const filteredProjects = projectsList.filter(p =>
    (p.project_name ?? '').toLowerCase().includes(projectSearch.toLowerCase())
  );

  // ── Step 1 render (Authorize) ─────────────────────────────────────────────

  function renderStep1() {
    const status = getStep(1).status;
    if (status === 'success') {
      return (
        <div className="selection-summary">
          <strong>Authorized as:</strong> {userInfo?.user_email || 'Unknown user'} &nbsp;|&nbsp;
          <strong>Token:</strong> {authToken.length} characters
        </div>
      );
    }
    return (
      <div className="auth-token-panel">
        <div className="auth-token-label">🔐 Get an access token, or paste one you already have</div>
        <div className="report-email-login-row">
          <input
            type="email"
            className="auth-token-input"
            placeholder="Enter your registered email…"
            value={emailInput}
            onChange={e => { setEmailInput(e.target.value); setEmailStatus({ type: 'idle', message: '' }); }}
            onKeyDown={e => { if (e.key === 'Enter') handleEmailLogin(); }}
          />
          <button
            className="auth-token-btn"
            onClick={handleEmailLogin}
            disabled={emailStatus.type === 'loading' || !emailInput.trim()}
          >
            {emailStatus.type === 'loading' ? '⏳ Loading...' : '🔑 Get Token'}
          </button>
        </div>
        {emailStatus.type !== 'idle' && (
          <p className={`report-email-status ${emailStatus.type}`}>{emailStatus.message}</p>
        )}

        <div className="report-auth-divider"><span>or enter token manually</span></div>

        <div className="auth-token-row">
          <input
            type="password"
            className="auth-token-input"
            placeholder="Paste your JWT token here…"
            value={localToken}
            onChange={e => setLocalToken(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && localToken.trim()) handleAuthorize(localToken.trim()); }}
          />
          <button
            className="auth-token-btn"
            onClick={() => { if (localToken.trim()) handleAuthorize(localToken.trim()); }}
            disabled={status === 'running' || !localToken.trim()}
          >
            {status === 'running' ? '⏳ Verifying...' : 'Authorize →'}
          </button>
        </div>
        {status === 'error' && getStep(1).errorMessage && (
          <p className="report-email-status error">{getStep(1).errorMessage}</p>
        )}
      </div>
    );
  }

  // ── Step 2 render (Select Project/Layout/Config) ─────────────────────────

  function renderStep2() {
    const status = getStep(2).status;
    if (status === 'success') {
      return (
        <div className="selection-summary">
          <strong>Project:</strong> {selectedProject?.project_name} &nbsp;|&nbsp;
          <strong>Layout:</strong> {selectedLayout?.name} &nbsp;|&nbsp;
          <strong>Configs:</strong> {selectedConfigIds.size} selected
        </div>
      );
    }
    return (
      <>
        {/* Stage A — Project */}
        <div className="cascade-stage">
          <div className="cascade-label">
            <span className="stage-num">A</span>Select Project
          </div>
          <div ref={projDropRef} className="cascade-dropdown-wrapper">
            <button
              className={`cascade-selected ${projDropOpen ? 'open' : ''}`}
              onClick={() => setProjDropOpen(o => !o)}
              disabled={!authToken}
            >
              <span className={selectedProject ? '' : 'placeholder'}>
                {selectedProject?.project_name ?? (projectsLoading ? 'Loading projects…' : 'Choose a project…')}
              </span>
              <span className="cascade-arrow">▼</span>
            </button>
            {projDropOpen && (
              <div className="cascade-dropdown">
                <input
                  className="cascade-search"
                  type="text"
                  placeholder="Search projects…"
                  value={projectSearch}
                  onChange={e => setProjectSearch(e.target.value)}
                  autoFocus
                />
                <div className="cascade-list">
                  {projectsLoading
                    ? <div className="cascade-loading">Loading…</div>
                    : filteredProjects.length === 0
                      ? <div className="cascade-empty">No projects found.</div>
                      : filteredProjects.map(p => (
                        <div
                          key={p.id}
                          className={`cascade-option ${selectedProject?.id === p.id ? 'active' : ''}`}
                          onClick={() => handleProjectSelect(p)}
                        >{p.project_name}</div>
                      ))
                  }
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stage B — Layout */}
        <div className="cascade-stage">
          <div className="cascade-label">
            <span className="stage-num">B</span>Select Layout
          </div>
          {!selectedProject
            ? <div className="cascade-disabled">🔒 Select a project first</div>
            : (
              <div ref={layoutDropRef} className="cascade-dropdown-wrapper">
                <button
                  className={`cascade-selected ${layoutDropOpen ? 'open' : ''}`}
                  onClick={() => setLayoutDropOpen(o => !o)}
                >
                  <span className={selectedLayout ? '' : 'placeholder'}>
                    {selectedLayout?.name ?? (layoutsLoading ? 'Loading layouts…' : 'Choose a layout…')}
                  </span>
                  <span className="cascade-arrow">▼</span>
                </button>
                {layoutDropOpen && (
                  <div className="cascade-dropdown">
                    <div className="cascade-list">
                      {layoutsLoading
                        ? <div className="cascade-loading">Loading…</div>
                        : layoutsList.length === 0
                          ? <div className="cascade-empty">No layouts for this project.</div>
                          : layoutsList.map(l => (
                            <div
                              key={l.id}
                              className={`cascade-option ${selectedLayout?.id === l.id ? 'active' : ''}`}
                              onClick={() => handleLayoutSelect(l)}
                            >{l.name}</div>
                          ))
                      }
                    </div>
                  </div>
                )}
              </div>
            )
          }
        </div>

        {/* Stage C — Configurations */}
        <div className="cascade-stage">
          <div className="cascade-label">
            <span className="stage-num">C</span>Select Configurations (up to 9)
          </div>
          {!selectedLayout
            ? <div className="cascade-disabled">🔒 Select a layout first</div>
            : configsLoading
              ? <div className="cascade-loading" style={{ padding: '10px 0' }}>Loading configurations…</div>
              : configsList.length === 0
                ? <div style={{ fontSize: '0.85rem', color: '#aaa', padding: '8px 0' }}>
                    No completed configurations for this layout.
                  </div>
                : (
                  <div className="config-checklist">
                    {configsList.map(cfg => {
                      const sel = selectedConfigIds.has(cfg.id);
                      return (
                        <div
                          key={cfg.id}
                          className={`config-checklist-item ${sel ? 'selected' : ''}`}
                          onClick={() => toggleConfig(cfg.id)}
                        >
                          <input
                            type="checkbox"
                            checked={sel}
                            onChange={() => toggleConfig(cfg.id)}
                            onClick={e => e.stopPropagation()}
                          />
                          <div className="config-item-info">
                            <div className="config-item-name">{cfg.configuration_name}</div>
                            {cfg.jobs_count != null && (
                              <div className="config-item-meta">{cfg.jobs_count} jobs</div>
                            )}
                            {renderConfigInputSummary(cfg.id)}
                          </div>
                        </div>
                      );
                    })}
                    <div className="config-limit-note">{selectedConfigIds.size} / 9 selected</div>
                  </div>
                )
          }
        </div>

        <button
          className="report-execute-btn"
          onClick={confirmSelection}
          disabled={!selectedProject || !selectedLayout || selectedConfigIds.size === 0}
        >
          Confirm Selection →
        </button>
      </>
    );
  }

  // ── Step 3 render (Select Figures) ────────────────────────────────────────

  function renderStep3() {
    const { status } = getStep(3);
    const configIdArr = Array.from(selectedConfigIds);

    if (status === 'success') {
      return (
        <div className="selection-summary">
          {selectedFigureConfigIds.size} configuration{selectedFigureConfigIds.size !== 1 ? 's' : ''} selected for report figures.
        </div>
      );
    }

    return (
      <>
        <p style={{ fontSize: '0.87rem', color: '#555', marginBottom: 16 }}>
          Choose up to {MAX_FIGURE_CONFIGS} configurations to feature as figures (heatmap + pile
          binning) in the generated report. The configurations you pick here are flagged in the
          Prerequisites and Pile Binning steps below.
        </p>

        <div className="config-checklist">
          {configIdArr.map(id => {
            const cfg = configsList.find(c => c.id === id);
            const checked = selectedFigureConfigIds.has(id);
            const disableCheck = !checked && selectedFigureConfigIds.size >= MAX_FIGURE_CONFIGS;
            return (
              <div
                key={id}
                className={`config-checklist-item ${checked ? 'selected' : ''}`}
                onClick={() => { if (!disableCheck) toggleFigureConfig(id); }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disableCheck}
                  onChange={() => toggleFigureConfig(id)}
                  onClick={e => e.stopPropagation()}
                />
                <div className="config-item-info">
                  <div className="config-item-name">{cfg?.configuration_name ?? `Config ${id}`}</div>
                  {renderConfigInputSummary(id)}
                </div>
              </div>
            );
          })}
          <div className="config-limit-note">{selectedFigureConfigIds.size} / {MAX_FIGURE_CONFIGS} selected</div>
        </div>

        <button
          className="report-execute-btn"
          style={{ marginTop: 24 }}
          onClick={confirmFigureSelection}
          disabled={selectedFigureConfigIds.size === 0}
        >
          Confirm Figure Selection →
        </button>
      </>
    );
  }

  // ── Step 4 render ─────────────────────────────────────────────────────────

  function renderStep4() {
    const { status, errorMessage } = getStep(4);
    const prereqList = Object.values(prereqItems);
    return (
      <>
        <p style={{ fontSize: '0.87rem', color: '#555', marginBottom: 16 }}>
          Each figure-selected configuration needs heatmap layers before report generation.
          Pile-reveal data is optional and enhances the pile summary tables. Only the
          configuration{selectedFigureConfigIds.size !== 1 ? 's' : ''} chosen for figures are
          checked here.
        </p>

        {status === 'ready' && (
          <button className="report-execute-btn" onClick={startPrereqs}>
            Run Prerequisite Checks
          </button>
        )}

        {prereqList.length > 0 && (
          <>
            <table className="prereq-table">
              <thead>
                <tr>
                  <th>Configuration</th>
                  <th>Heatmap Layers</th>
                  <th>Pile Reveal</th>
                </tr>
              </thead>
              <tbody>
                {prereqList.map(item => (
                  <tr key={item.configId}>
                    <td>{item.configName}</td>
                    <td>
                      <div className="prereq-cell-status">
                        <span className={`prereq-dot ${item.heatmapStatus}`} />
                        <span className="prereq-status-text">{PREREQ_LABELS[item.heatmapStatus]}</span>
                      </div>
                    </td>
                    <td>
                      <div className="prereq-cell-status">
                        <span className={`prereq-dot ${item.pileRevealStatus}`} />
                        <span className="prereq-status-text">{PREREQ_LABELS[item.pileRevealStatus]}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {status === 'running' && (
              <div className="report-step-loading">
                <div className="report-spinner" />
                Polling for completion…
              </div>
            )}

            {heatmapStatusApiLog && (
              <div className="report-response-box">
                <strong className="report-api-box-title">Heatmap Status API</strong>
                <details className="report-api-io">
                  <summary>View Request Payload</summary>
                  <pre>{JSON.stringify(heatmapStatusApiLog.request, null, 2)}</pre>
                </details>
                {heatmapStatusApiLog.response && (
                  <details className="report-api-io">
                    <summary>View Response Data</summary>
                    <pre>{JSON.stringify(heatmapStatusApiLog.response, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}

            {prereqList.map(item => pileRevealApiLog[item.configId] && (
              <div className="report-response-box" key={`pile-reveal-log-${item.configId}`}>
                <strong className="report-api-box-title">Pile Reveal API — {item.configName}</strong>
                <details className="report-api-io">
                  <summary>View Request Payload</summary>
                  <pre>{JSON.stringify(pileRevealApiLog[item.configId].request, null, 2)}</pre>
                </details>
                {pileRevealApiLog[item.configId].response && (
                  <details className="report-api-io">
                    <summary>View Response Data</summary>
                    <pre>{JSON.stringify(pileRevealApiLog[item.configId].response, null, 2)}</pre>
                  </details>
                )}
              </div>
            ))}
          </>
        )}

        {status === 'error' && (
          <>
            <div className="report-error-box"><p>{errorMessage}</p></div>
            <button className="report-retry-btn" onClick={retryPrereqs}>Retry</button>
          </>
        )}
      </>
    );
  }

  // ── Step 5: Additional pile bin helpers ──────────────────────────────────

  function assignColorsToData(entry: PileBinningEntry): PileBinningEntry {
    const binsMap: Record<string, any[]> = entry.analysis[0]?.imported_data ?? {};
    const src = (entry.source ?? '').toUpperCase();
    const isCSV   = src === 'CSV' || src === 'XLSX';
    const isDpDxf = src === 'DP' || src === 'DP-LAYOUT' || src === 'DXF';
    let paletteIdx = 0;
    const updated: Record<string, any[]> = {};
    for (const key of Object.keys(binsMap)) {
      let baseColor: string | undefined;
      if (isCSV)   baseColor = CSV_PILE_TYPE_COLORS[key];
      if (isDpDxf) baseColor = DP_DXF_PILE_TYPE_COLORS[key];
      if (!baseColor) {
        baseColor = paletteIdx < PILE_COLORS_PALETTE.length
          ? PILE_COLORS_PALETTE[paletteIdx++]
          : colorFromNameHash(key);
        if (!isCSV && !isDpDxf) paletteIdx = Math.min(paletteIdx, PILE_COLORS_PALETTE.length);
      }
      updated[key] = (binsMap[key] as any[]).map((b: any) => b.color ? b : { ...b, color: baseColor });
    }
    const newAnalysis = entry.analysis.map((a, i) => i === 0 ? { ...a, imported_data: updated } : a);
    return { ...entry, analysis: newAnalysis };
  }

  async function triggerPileBinCalc(configId: number, pileKey: string, binIdx: number) {
    const entry = pileBinsRef.current[configId];
    if (!entry) return;
    const bin: any = entry.analysis[0]?.imported_data?.[pileKey]?.[binIdx];
    if (!bin) return;
    try {
      const r = await fetch(`${API_BASE_URL}/tools/api/v1/pile-binning/pile_binning_calculation`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authTokenRef.current}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          minimum_embedment: bin.minimum_embedment ?? 0,
          end: bin.end,
          unit: entry.unit,
          section_type: bin.section_type ?? '',
          count: bin.count,
          config_id: configId,
        }),
      });
      if (!r.ok) return;
      const d = await r.json();
      const [pile_length, total_weight] = Array.isArray(d) ? d : [d.pile_length, d.total_weight];
      setPileBins(prev => {
        const e = prev[configId];
        if (!e) return prev;
        const bm = { ...(e.analysis[0]?.imported_data ?? {}) };
        const arr = [...(bm[pileKey] ?? [])];
        arr[binIdx] = { ...arr[binIdx], pile_length, total_weight };
        bm[pileKey] = arr;
        const na = e.analysis.map((a, i) => i === 0 ? { ...a, imported_data: bm } : a);
        const updated = { ...e, analysis: na };
        autoSavePileBins(configId, updated);
        return { ...prev, [configId]: updated };
      });
    } catch { /* silent */ }
  }

  function handleResetEmbed(configId: number, pileKey: string, binIdx: number) {
    const entry = pileBinsRef.current[configId];
    if (!entry) return;
    const bin: any = entry.analysis[0]?.imported_data?.[pileKey]?.[binIdx];
    if (!bin) return;
    const pct = bin.percentage ?? 0;
    const groupBins: any[] = entry.analysis[0]?.imported_data?.[pileKey] ?? [];
    const groupIdx = groupBins.indexOf(bin);
    let minimum_embedment: number;
    let section_type: string;
    if (pct <= 6)       { minimum_embedment = 1.50; section_type = 'W6x20'; }
    else if (pct <= 15) { minimum_embedment = 1.25; section_type = 'W6x15'; }
    else {
      if (groupIdx === 0)      { minimum_embedment = 1.00; section_type = 'W6x10.4'; }
      else if (groupIdx === 1) { minimum_embedment = 1.05; section_type = 'W6x12'; }
      else                     { minimum_embedment = 1.15; section_type = 'W6x15'; }
    }
    setPileBins(prev => {
      const e = prev[configId];
      if (!e) return prev;
      const bm = { ...(e.analysis[0]?.imported_data ?? {}) };
      const arr = [...(bm[pileKey] ?? [])];
      arr[binIdx] = { ...arr[binIdx], minimum_embedment, section_type };
      bm[pileKey] = arr;
      const na = e.analysis.map((a, i) => i === 0 ? { ...a, imported_data: bm } : a);
      const updated = { ...e, analysis: na };
      autoSavePileBins(configId, updated);
      return { ...prev, [configId]: updated };
    });
    setTimeout(() => triggerPileBinCalc(configId, pileKey, binIdx), 0);
  }

  function handleCategoryChange(configId: number, pileKey: string, binIdx: number, value: string) {
    const key = `${configId}|${pileKey}|${binIdx}|cat`;
    const t = catTimers.current.get(key);
    if (t) clearTimeout(t);
    setPileBins(prev => {
      const e = prev[configId];
      if (!e) return prev;
      const bm = { ...(e.analysis[0]?.imported_data ?? {}) };
      const arr = [...(bm[pileKey] ?? [])];
      arr[binIdx] = { ...arr[binIdx], category: value };
      bm[pileKey] = arr;
      const na = e.analysis.map((a, i) => i === 0 ? { ...a, imported_data: bm } : a);
      return { ...prev, [configId]: { ...e, analysis: na } };
    });
    catTimers.current.set(key, setTimeout(() => {
      const e = pileBinsRef.current[configId];
      if (e) autoSavePileBins(configId, e);
    }, 1000));
  }

  function handleLabelChange(configId: number, pileKey: string, binIdx: number, value: string) {
    const key = `${configId}|${pileKey}|${binIdx}|lbl`;
    const t = labelTimers.current.get(key);
    if (t) clearTimeout(t);
    setPileBins(prev => {
      const e = prev[configId];
      if (!e) return prev;
      const bm = { ...(e.analysis[0]?.imported_data ?? {}) };
      const arr = [...(bm[pileKey] ?? [])];
      arr[binIdx] = { ...arr[binIdx], name: value };
      bm[pileKey] = arr;
      const na = e.analysis.map((a, i) => i === 0 ? { ...a, imported_data: bm } : a);
      return { ...prev, [configId]: { ...e, analysis: na } };
    });
    labelTimers.current.set(key, setTimeout(() => {
      const e = pileBinsRef.current[configId];
      if (e) autoSavePileBins(configId, e);
    }, 1000));
  }

  function handleSectionTypeChange(configId: number, pileKey: string, binIdx: number, value: string) {
    setPileBins(prev => {
      const e = prev[configId];
      if (!e) return prev;
      const bm = { ...(e.analysis[0]?.imported_data ?? {}) };
      const arr = [...(bm[pileKey] ?? [])];
      arr[binIdx] = { ...arr[binIdx], section_type: value };
      bm[pileKey] = arr;
      const na = e.analysis.map((a, i) => i === 0 ? { ...a, imported_data: bm } : a);
      const updated = { ...e, analysis: na };
      autoSavePileBins(configId, updated);
      return { ...prev, [configId]: updated };
    });
    setTimeout(() => triggerPileBinCalc(configId, pileKey, binIdx), 0);
  }

  function handleEmbedmentChange(configId: number, pileKey: string, binIdx: number, value: string) {
    const num = parseFloat(value);
    setPileBins(prev => {
      const e = prev[configId];
      if (!e) return prev;
      const bm = { ...(e.analysis[0]?.imported_data ?? {}) };
      const arr = [...(bm[pileKey] ?? [])];
      arr[binIdx] = { ...arr[binIdx], minimum_embedment: isNaN(num) ? 0 : num };
      bm[pileKey] = arr;
      const na = e.analysis.map((a, i) => i === 0 ? { ...a, imported_data: bm } : a);
      return { ...prev, [configId]: { ...e, analysis: na } };
    });
  }

  function handleEmbedmentBlur(configId: number, pileKey: string, binIdx: number) {
    const e = pileBinsRef.current[configId];
    if (e) autoSavePileBins(configId, e);
    triggerPileBinCalc(configId, pileKey, binIdx);
  }

  // ── Step 5 render (Pile Binning) ──────────────────────────────────────────

  function renderStep5() {
    const { status } = getStep(5);
    const configIdArr = Array.from(selectedFigureConfigIds);

    if (status === 'success') {
      return (
        <div className="selection-summary">
          Pile binning configured for {configIdArr.length} configuration{configIdArr.length !== 1 ? 's' : ''}.
        </div>
      );
    }

    const allLoaded = configIdArr.every(id => pileBins[id] && !pileBinsLoading[id]);
    const anyError  = configIdArr.some(id => pileBinsError[id]);

    return (
      <>
        <p style={{ fontSize: '0.87rem', color: '#555', marginBottom: 16 }}>
          Review and edit pile bins. Change colors, split ranges, set embedment and section types.
          Changes are auto-saved.
        </p>

        {configIdArr.length > 1 && (
          <div className="pb-tabs">
            {configIdArr.map((id, i) => {
              const cfg = configsList.find(c => c.id === id);
              return (
                <button
                  key={id}
                  className={`pb-tab ${activePileBinTab === i ? 'active' : ''}`}
                  onClick={() => { setActivePileBinTab(i); setSplitEditing(null); setSplitInputVal(''); }}
                >
                  {cfg?.configuration_name ?? `Config ${id}`}
                  {selectedFigureConfigIds.has(id) && (
                    <span className="figure-badge" title="Selected as report figure">★</span>
                  )}
                  {pileBinsLoading[id] && <span className="pb-tab-spinner" />}
                </button>
              );
            })}
          </div>
        )}

        {configIdArr.map((configId, tabIdx) => {
          if (tabIdx !== activePileBinTab) return null;
          const loading = pileBinsLoading[configId];
          const error   = pileBinsError[configId];
          const rawEntry = pileBins[configId];
          const entry   = rawEntry ? assignColorsToData(rawEntry) : rawEntry;
          const binsMap: Record<string, PileBin[]> = entry?.analysis[0]?.imported_data ?? {};
          const pileKeys = Object.keys(binsMap);
          const allBins  = (Object.values(binsMap) as PileBin[][]).flat();
          const totalCount  = allBins.reduce((s, b) => s + (b.count ?? 0), 0);
          const totalWeight = allBins.reduce((s, b) => s + ((b as any).total_weight ?? 0), 0);

          return (
            <div key={configId}>
              {configIdArr.length === 1 && selectedFigureConfigIds.has(configId) && (
                <div className="figure-flag-note">
                  <span className="figure-badge" title="Selected as report figure">★</span>
                  Selected as a report figure
                </div>
              )}
              {loading && (
                <div className="report-step-loading">
                  <div className="report-spinner" />
                  Loading pile binning data…
                </div>
              )}
              {error && (
                <div className="report-error-box">
                  <p>{error}</p>
                  <button className="report-retry-btn" onClick={() => {
                    setPileBins(prev => { const n = { ...prev }; delete n[configId]; return n; });
                    setPileBinsError(prev => ({ ...prev, [configId]: null }));
                    loadPileBinningForConfig(configId);
                  }}>Retry</button>
                </div>
              )}

              {pileBinningApiLog[configId] && (
                <div className="report-response-box">
                  <strong className="report-api-box-title">Pile Binning API</strong>
                  <details className="report-api-io">
                    <summary>View Request Payload</summary>
                    <pre>{JSON.stringify(pileBinningApiLog[configId].request, null, 2)}</pre>
                  </details>
                  {pileBinningApiLog[configId].response && (
                    <details className="report-api-io">
                      <summary>View Response Data</summary>
                      <pre>{JSON.stringify(pileBinningApiLog[configId].response, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}

              {autoSaveApiLog[configId] && (
                <div className="report-response-box">
                  <strong className="report-api-box-title">Auto Save API</strong>
                  <details className="report-api-io">
                    <summary>View Request Payload</summary>
                    <pre>{JSON.stringify(autoSaveApiLog[configId].request, null, 2)}</pre>
                  </details>
                  {autoSaveApiLog[configId].response && (
                    <details className="report-api-io">
                      <summary>View Response Data</summary>
                      <pre>{JSON.stringify(autoSaveApiLog[configId].response, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}

              {splitApiLog[configId] && (
                <div className="report-response-box">
                  <strong className="report-api-box-title">Split API</strong>
                  <details className="report-api-io">
                    <summary>View Request Payload</summary>
                    <pre>{JSON.stringify(splitApiLog[configId].request, null, 2)}</pre>
                  </details>
                  {splitApiLog[configId].response && (
                    <details className="report-api-io">
                      <summary>View Response Data</summary>
                      <pre>{JSON.stringify(splitApiLog[configId].response, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}

              {!loading && !error && entry && (
                <div className="pc-wrapper">
                  {pileKeys.length === 0 ? (
                    <div className="pc-no-data">No pile configuration data available.</div>
                  ) : (
                    <table className="pc-table">
                      <colgroup>
                        <col className="pc-col-pile" />
                        <col className="pc-col-reveal" />
                        <col className="pc-col-cat" />
                        <col className="pc-col-label" />
                        <col className="pc-col-embed" />
                        <col className="pc-col-length" />
                        <col className="pc-col-section" />
                        <col className="pc-col-weight" />
                        <col className="pc-col-actions" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="pc-th pc-col-pile">Pile type</th>
                          <th className="pc-th pc-col-reveal">Reveal range ({entry.unit})</th>
                          <th className="pc-th pc-col-cat">Category</th>
                          <th className="pc-th pc-col-label">Label</th>
                          <th className="pc-th pc-col-embed">Min. emb. ({entry.unit})</th>
                          <th className="pc-th pc-col-length">Length ({entry.unit})</th>
                          <th className="pc-th pc-col-section">Section</th>
                          <th className="pc-th pc-col-weight">Weight (lbs)</th>
                          <th className="pc-th pc-col-actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {pileKeys.flatMap(pileKey => {
                          const bins = binsMap[pileKey] ?? [];
                          return (bins as any[]).flatMap((bin: any, binIdx: number) => {
                            const isSplitting = splitEditing?.configId === configId
                              && splitEditing.pileKey === pileKey
                              && splitEditing.binIdx === binIdx;
                            const splitNum = parseFloat(splitInputVal);
                            const splitInvalid = splitInputVal !== ''
                              && (isNaN(splitNum) || splitNum <= bin.start || splitNum >= bin.end);
                            const refKey = `${configId}|${pileKey}|${binIdx}`;

                            const mainRow = (
                              <tr key={`${pileKey}-${binIdx}-row`} className="pc-data-row">
                                <td className="pc-col-pile">
                                  <div className="pc-pile-cell">
                                    <div className="pc-pile-top">
                                      <span className="pc-pile-name">{pileKey}</span>
                                    </div>
                                    <div
                                      className="pc-pile-bar"
                                      style={{
                                        width: `${Math.min(100, Math.max(5, bin.percentage ?? 5))}%`,
                                        backgroundColor: bin.color || '#4895EF',
                                      }}
                                      onClick={() => colorInputRefs.current.get(refKey)?.click()}
                                      title="Click to change color"
                                    />
                                    <input
                                      ref={el => {
                                        if (el) colorInputRefs.current.set(refKey, el);
                                        else colorInputRefs.current.delete(refKey);
                                      }}
                                      type="color"
                                      className="pc-color-trigger"
                                      defaultValue={bin.color || '#4895EF'}
                                      onChange={e => handleBinColorChange(configId, pileKey, binIdx, e.target.value)}
                                    />
                                  </div>
                                </td>
                                <td className="pc-col-reveal">
                                  <div className="pc-reveal-cell">
                                    <span className="pc-reveal-range">
                                      [{bin.start.toFixed(3)}, {bin.end.toFixed(3)}]
                                    </span>
                                    <button
                                      className="pc-split-btn"
                                      title="Split range"
                                      onClick={() => {
                                        setSplitEditing({ configId, pileKey, binIdx });
                                        setSplitInputVal('');
                                      }}
                                    >+</button>
                                  </div>
                                </td>
                                <td className="pc-col-cat">
                                  <input
                                    key={`cat-${configId}-${pileKey}-${binIdx}`}
                                    className="pc-input"
                                    type="text"
                                    defaultValue={bin.category ?? ''}
                                    onChange={e => handleCategoryChange(configId, pileKey, binIdx, e.target.value)}
                                    placeholder="Enter category..."
                                  />
                                </td>
                                <td className="pc-col-label">
                                  <input
                                    key={`lbl-${configId}-${pileKey}-${binIdx}`}
                                    className="pc-input"
                                    type="text"
                                    defaultValue={bin.name ?? ''}
                                    onChange={e => handleLabelChange(configId, pileKey, binIdx, e.target.value)}
                                    placeholder="Enter name..."
                                  />
                                </td>
                                <td className="pc-col-embed">
                                  <div className="pc-embed-cell">
                                    <input
                                      key={`emb-${configId}-${pileKey}-${binIdx}-${bin.minimum_embedment}`}
                                      className="pc-input pc-input-num"
                                      type="number"
                                      step="0.001"
                                      defaultValue={bin.minimum_embedment != null ? Number(bin.minimum_embedment).toFixed(3) : ''}
                                      onChange={e => handleEmbedmentChange(configId, pileKey, binIdx, e.target.value)}
                                      onBlur={() => handleEmbedmentBlur(configId, pileKey, binIdx)}
                                    />
                                    <button
                                      className="pc-reset-embed-btn"
                                      title="Reset to calculated value"
                                      onClick={() => handleResetEmbed(configId, pileKey, binIdx)}
                                    >↺</button>
                                  </div>
                                </td>
                                <td className="pc-col-length pc-readonly pc-length-val">
                                  {bin.pile_length != null ? Number(bin.pile_length).toFixed(3) : '—'}
                                </td>
                                <td className="pc-col-section">
                                  <select
                                    className="pc-select"
                                    value={bin.section_type ?? ''}
                                    onChange={e => handleSectionTypeChange(configId, pileKey, binIdx, e.target.value)}
                                  >
                                    <option value="" disabled>—</option>
                                    {STEEL_SECTIONS.map(s => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="pc-col-weight pc-readonly pc-num">
                                  {bin.total_weight != null ? Math.round(bin.total_weight).toLocaleString() : '—'}
                                </td>
                                <td className="pc-col-actions">
                                  {bins.length > 1 && binIdx > 0 && (
                                    <button
                                      className="pc-delete-btn"
                                      title="Delete bin"
                                      onClick={() => handleDeleteBin(configId, pileKey, binIdx)}
                                    >🗑</button>
                                  )}
                                </td>
                              </tr>
                            );

                            if (!isSplitting) return [mainRow];

                            return [
                              mainRow,
                              <tr key={`${pileKey}-${binIdx}-editor`} className="pc-editor-row">
                                <td className="pc-editor-cell" colSpan={9}>
                                  <div className="pc-editor-container">
                                    <span className="pc-editor-hint">
                                      Enter a value between {bin.start.toFixed(3)} and {bin.end.toFixed(3)}
                                    </span>
                                    <input
                                      className={`pc-editor-input${splitInvalid ? ' pc-editor-input--invalid' : ''}`}
                                      type="text"
                                      value={splitInputVal}
                                      onChange={e => setSplitInputVal(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') handleSplitBin(configId, pileKey, binIdx);
                                        if (e.key === 'Escape') { setSplitEditing(null); setSplitInputVal(''); }
                                      }}
                                      autoFocus
                                    />
                                    <button
                                      className="pc-editor-split-btn"
                                      disabled={splitInvalid || splitInputVal === ''}
                                      onClick={() => handleSplitBin(configId, pileKey, binIdx)}
                                    >Split</button>
                                    <button
                                      className="pc-editor-cancel-btn"
                                      onClick={() => { setSplitEditing(null); setSplitInputVal(''); }}
                                    >Cancel</button>
                                  </div>
                                </td>
                              </tr>,
                            ];
                          });
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="pc-total-row">
                          <td className="pc-col-pile pc-total-label"><strong>Total:</strong></td>
                          <td className="pc-col-reveal pc-num">
                            <strong>{totalCount.toLocaleString()}</strong>
                          </td>
                          <td className="pc-col-cat" />
                          <td className="pc-col-label" />
                          <td className="pc-col-embed" />
                          <td className="pc-col-length" />
                          <td className="pc-col-section" />
                          <td className="pc-col-weight pc-num">
                            <strong>{Math.round(totalWeight).toLocaleString()}</strong>
                          </td>
                          <td className="pc-col-actions" />
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <button
          className="report-execute-btn"
          style={{ marginTop: 24 }}
          onClick={confirmPileBinning}
          disabled={!allLoaded || anyError}
        >
          Confirm Pile Binning →
        </button>
      </>
    );
  }

  // ── Step 6 render (Configure Report) ─────────────────────────────────────

  function renderStep6() {
    const { status } = getStep(6);
    if (status === 'success') {
      return (
        <div className="selection-summary">
          <strong>Type:</strong> Advanced &nbsp;|&nbsp;
          <strong>Format:</strong> DOCX (Template)
          {projName ? ' | Metadata: set' : ''}
        </div>
      );
    }
    return (
      <div className="report-config-form">
        {/* Project metadata */}
        <div className="collapsible-section">
          <div className={`collapsible-header ${metaOpen ? 'open' : ''}`} onClick={() => setMetaOpen(o => !o)}>
            <span>Project Metadata (optional)</span>
            <span className="toggle-arrow">▼</span>
          </div>
          {metaOpen && (
            <div className="collapsible-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Project Name</label>
                  <input className="form-input" value={projName} onChange={e => setProjName(e.target.value)} placeholder="e.g. Solar Farm Alpha" />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input className="form-input" value={projLocation} onChange={e => setProjLocation(e.target.value)} placeholder="e.g. Nevada, USA" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Coordinate System</label>
                  <input className="form-input" value={coordSys} onChange={e => setCoordSys(e.target.value)} placeholder="e.g. WGS84 / UTM Zone 11N" />
                </div>
                <div className="form-group" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input className="form-input" value={latitude} onChange={e => setLatitude(e.target.value)} placeholder="e.g. 36.1699" />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input className="form-input" value={longitude} onChange={e => setLongitude(e.target.value)} placeholder="e.g. -115.1398" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input files */}
        <div className="collapsible-section">
          <div className={`collapsible-header ${filesOpen ? 'open' : ''}`} onClick={() => setFilesOpen(o => !o)}>
            <span>Input Files (optional)</span>
            <span className="toggle-arrow">▼</span>
          </div>
          {filesOpen && (
            <div className="collapsible-body">
              <div style={{ overflowX: 'auto' }}>
                <table className="dynamic-table">
                  <thead>
                    <tr><th>Title / Filename</th><th>Format</th><th>Date</th><th /></tr>
                  </thead>
                  <tbody>
                    {inputFiles.map((f, i) => (
                      <tr key={i}>
                        <td><input value={f.filename} onChange={e => editInputFile(i, 'filename', e.target.value)} placeholder="e.g., terrain_data.tif" /></td>
                        <td><input value={f.format}   onChange={e => editInputFile(i, 'format',   e.target.value)} placeholder="e.g., GeoTIFF" /></td>
                        <td><input type="date" value={f.dated} onChange={e => editInputFile(i, 'dated', e.target.value)} /></td>
                        <td><button className="row-del-btn" onClick={() => delInputFile(i)} disabled={inputFiles.length === 1}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="table-add-btn" onClick={addInputFile}>+ Add Row</button>
            </div>
          )}
        </div>

        {/* Revision details */}
        <div className="collapsible-section">
          <div className={`collapsible-header ${revOpen ? 'open' : ''}`} onClick={() => setRevOpen(o => !o)}>
            <span>Revision Details (optional)</span>
            <span className="toggle-arrow">▼</span>
          </div>
          {revOpen && (
            <div className="collapsible-body">
              <div style={{ overflowX: 'auto' }}>
                <table className="dynamic-table">
                  <thead>
                    <tr><th>Rev</th><th>Date</th><th>Description</th><th>Prepared By</th><th>Checked By</th><th>Approved By</th><th /></tr>
                  </thead>
                  <tbody>
                    {revisions.map((rv, i) => (
                      <tr key={i}>
                        <td><input value={rv.rev}         onChange={e => editRevision(i, 'rev',         e.target.value)} /></td>
                        <td><input type="date" value={rv.date} onChange={e => editRevision(i, 'date', e.target.value)} /></td>
                        <td><input value={rv.description} onChange={e => editRevision(i, 'description', e.target.value)} /></td>
                        <td><input value={rv.preparedBy}  onChange={e => editRevision(i, 'preparedBy',  e.target.value)} /></td>
                        <td><input value={rv.checkedBy}   onChange={e => editRevision(i, 'checkedBy',   e.target.value)} /></td>
                        <td><input value={rv.approvedBy}  onChange={e => editRevision(i, 'approvedBy',  e.target.value)} /></td>
                        <td><button className="row-del-btn" onClick={() => delRevision(i)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button className="table-add-btn" onClick={addRevision}>+ Add Row</button>
            </div>
          )}
        </div>

        <button className="report-execute-btn" onClick={confirmReportConfig}>
          Confirm Configuration →
        </button>
      </div>
    );
  }

  // ── Step 7 render (Generate Report) ───────────────────────────────────────

  function renderStep7() {
    const { status, errorMessage, requestData, responseData } = getStep(7);
    return (
      <>
        {status === 'ready' && (
          <>
            <p style={{ fontSize: '0.87rem', color: '#555', marginBottom: 16 }}>
              Generate and download the advanced report DOCX. The file will download automatically
              once ready.
            </p>
            <button className="report-execute-btn" onClick={generateReport}>
              Generate Report
            </button>
          </>
        )}

        {status === 'running' && (
          <div className="report-step-loading">
            <div className="report-spinner" />
            <div style={{ flex: 1 }}>
              <span>{reportStatusMsg || 'Generating report… this may take a minute.'}</span>
              {Object.keys(requestData).length > 0 && (
                <details className="report-api-io">
                  <summary>View Request Payload</summary>
                  <pre>{JSON.stringify(requestData, null, 2)}</pre>
                </details>
              )}
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="report-response-box">
            <div className="report-success-line">
              <span style={{ fontSize: '1.5rem' }}>✓</span>
              <span>Report generated and downloaded as <strong>{downloadFilename}</strong></span>
            </div>
            <strong className="report-api-box-title">Generate Report API</strong>
            {Object.keys(requestData).length > 0 && (
              <details className="report-api-io">
                <summary>View Request Payload</summary>
                <pre>{JSON.stringify(requestData, null, 2)}</pre>
              </details>
            )}
            {responseData && (
              <details className="report-api-io">
                <summary>View Response Data</summary>
                <pre>{JSON.stringify(responseData, null, 2)}</pre>
              </details>
            )}
          </div>
        )}

        {status === 'error' && (
          <>
            <div className="report-error-box"><p>{errorMessage}</p></div>
            {Object.keys(requestData).length > 0 && (
              <details className="report-api-io">
                <summary>View Request Payload</summary>
                <pre>{JSON.stringify(requestData, null, 2)}</pre>
              </details>
            )}
            <button className="report-retry-btn" onClick={generateReport}>Retry</button>
          </>
        )}
      </>
    );
  }

  // ── Step 8 render (Download) ──────────────────────────────────────────────

  function renderStep8() {
    const { status, errorMessage } = getStep(8);
    return (
      <>
        {status === 'ready' && downloadUrl && (
          <div className="download-card">
            <div className="download-filename">📄 {downloadFilename}</div>
            <button className="report-execute-btn" onClick={fetchDownloadUrl}>
              Download Again
            </button>
            <button className="report-secondary-btn" onClick={startAnotherReport}>
              Download Another Report
            </button>
          </div>
        )}

        {status === 'running' && (
          <div className="report-step-loading">
            <div className="report-spinner" />
            Preparing download…
          </div>
        )}

        {status === 'success' && downloadUrl && (
          <div className="download-card">
            <div className="download-filename">📄 {downloadFilename}</div>
            <button className="report-execute-btn" onClick={fetchDownloadUrl}>
              Download Again
            </button>
            <button className="report-secondary-btn" onClick={startAnotherReport}>
              Download Another Report
            </button>
          </div>
        )}

        {status === 'error' && (
          <>
            <div className="report-error-box"><p>{errorMessage}</p></div>
            <button className="report-retry-btn" onClick={fetchDownloadUrl}>Retry</button>
          </>
        )}
      </>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  const stepRenderers = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5, renderStep6, renderStep7, renderStep8];

  return (
    <div className="report-demo">
      <div className="report-demo-intro">
        <h1>Advanced Report Demo</h1>
        <p>
          Generate a Terrain Pro advanced report through the API. Select a layout and its
          completed configurations, verify prerequisites (heatmap layers + pile reveal),
          configure report options, then generate and download the finished document.
        </p>
      </div>

      {/* Progress tracker */}
      <div className="report-progress-tracker">
        {steps.map((step, i) => {
          const done   = step.status === 'success';
          const active = step.status === 'ready' || step.status === 'running' || step.status === 'error';
          const cls = ['report-progress-step',
            done ? 'success' : active ? 'active' : '',
            step.status === 'error' ? 'error' : '',
          ].filter(Boolean).join(' ');
          return (
            <React.Fragment key={step.stepNumber}>
              <div className={cls}>
                <div className="step-circle">{done ? '✓' : step.stepNumber}</div>
                <div className="step-label">{PROGRESS_LABELS[i]}</div>
              </div>
              {i < steps.length - 1 && (
                <div className={`progress-connector ${done ? 'done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step cards */}
      {steps.map((step, i) => (
        <div
          key={step.stepNumber}
          ref={el => { stepRefs.current[i] = el; }}
          className={stepCardClass(step.stepNumber)}
        >
          <div className="report-step-header">
            <div className="report-step-title-row">
              <div className="report-step-number">
                {step.status === 'success' ? '✓' : step.stepNumber}
              </div>
              <span className="report-step-title">{STEP_TITLES[i]}</span>
            </div>
            <span className={badgeClass(step.status)}>{badgeLabel(step.status)}</span>
          </div>
          <div className="report-step-body">
            {step.status === 'locked'
              ? <div className="report-step-locked">🔒 Complete the previous step to unlock.</div>
              : stepRenderers[i]()
            }
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReportDemo;
